import { useViewAs } from '../composables/useViewAs'
import type { BacktestAssetId, BacktestConfigFund, Fund, PotFund } from '../components/nav/fundConfig'

const BASE = import.meta.env.VITE_API_URL ?? '';

// Exported (rather than module-private) purely so client.viewas.test.ts can
// exercise the header-merge/override-protection contract directly — no
// production caller needs this export; every real caller goes through the
// `api.*` methods below.
export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Linked users Task 9 (spec 2026-09-28), ruling 5: while a superuser is
  // viewing as another user, every request carries the target's userId so
  // the server can swap the data scope. Read fresh on every call (not
  // captured once) so a mid-session switch/clear takes effect immediately.
  const viewAsTarget = useViewAs().target.value
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  }
  if (viewAsTarget) headers['X-SideWinder-View-As'] = viewAsTarget.userId
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Identity pill bug fix (2026-09-29): the thrown Error carries the HTTP
    // status as a plain property so callers (useAuth's confirmed-401/403
    // check) can distinguish "the server told us who's signed in" (401/403)
    // from a network blip or 5xx — without this, ANY /me failure looked
    // identical and a transient outage wiped Paolo's view-as escape route.
    const err = new Error(body.error ?? `HTTP ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  // Handle empty bodies: 204 No Content, and 200 OK with no body (Minimal-API
  // `Results.Ok()` with no payload — e.g. PUT /api/risk/thresholds). Calling
  // res.json() on those throws "Unexpected end of JSON input"; return
  // undefined cast to T instead so request<void> callers don't blow up.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export interface HLAccount {
  accountId: number;
  name: string;
}

export interface FundPosition {
  sedol: string;
  accountId: number;
  unitsHeld: number | null;
  holding: number | null;
  available: number | null;
}

export interface DealConfirmation {
  status: string;
  reference: string | null;
  rawHtml: string | null;
  commission: number | null;
  stampDuty: number | null;
  ptmLevy: number | null;
  totalCharges: number | null;
}

export interface VixCurvePoint {
  symbol: string;
  expiry: string;
  price: number;
  month_label: string;
}

export interface VixTermStructure {
  fetch_date: string;
  m1_symbol: string;
  m1_expiry: string;
  m1_price: number;
  m2_symbol: string;
  m2_expiry: string;
  m2_price: number;
  spot_vix: number | null;
  contango_pct: number;
  spot_vs_m1_pct: number | null;
  structure: 'contango' | 'backwardation' | 'flat';
  structure_label: string;
  curve: VixCurvePoint[];
}

// Base fields produced by both BGA and JH signal engines.
interface BaseSignalData {
  fund: 'BGA' | 'JH';
  timestamp: string;
  signal: number | null;
  decision: string;
  gate_reason: string | null;
  vix: number | null;
  vix_regime: string | null;
  vix_term_structure: VixTermStructure | null;
  threshold: number | null;
  mr_zscore: number | null;
  mr_tilt_bps: number | null;
  synth_nav_return: number | null;
  synth_nav_return_gbp: number | null;
  synth_nav_coverage_pct: number | null;
  synth_nav_return_gbp_raw: number | null;
  gbpusd_rate: number | null;
  gbpusd_ref: number | null;
  gbpusd_ref_source: string | null;
  gbpusd_prev_close_t1: number | null;
  nas100_price: number | null;
  nas100_prev_close: number | null;
  nas100_prev_close_t1: number | null;
  nq_nav_prev_close: number | null;
  nq_t2_degraded: boolean | null;
  prev_close_date: string | null;
  prev_close_stale: boolean | null;
  vol_gate_active?: boolean | null;
  error: string | null;
  available?: boolean;
  // JH futures step-aside (live arc 2026-07-27; mirrors the promoted
  // jh_futures_driven preset's C# rule). All null/absent unless the active
  // preset sets step_aside_mode "pure"|"gate". s_on = ln(NQ@08:40-London /
  // prior US futures close); state "frozen" = the 08:40 print latched (the
  // decision input), "live" = pre-08:40 tracking, "degraded" = inputs
  // missing (fail-safe verdict: stay invested). Optional (older payloads).
  step_aside_mode?: 'pure' | 'gate' | null;
  step_aside_s_on?: number | null;
  step_aside_threshold_bps?: number | null;
  // 2026-09-15: BUY-side threshold when the dead-zone rule is active.
  // Undefined / equal to the sell threshold means the legacy one-sided
  // rule is in effect. SPA renders the band only when the two differ.
  step_aside_buy_threshold_bps?: number | null;
  step_aside_state?: 'live' | 'frozen' | 'degraded' | null;
  step_aside_frozen_at?: string | null;
  step_aside_nq_print?: number | null;
  step_aside_nq_prev_close?: number | null;
  // FVA gap-correction (spec 2026-07-13; fund-generic since 2026-07-27 —
  // mirrors the shared Python schema so JH's λ-ramp FVA needs no UI work
  // at promotion). All null unless the live preset sets
  // fva_signal_mode="gap_correct". The engine subtracts fva_correction
  // from the signal AFTER synth-NAV selection.
  fva_mode?: string | null;
  fva_m_t?: number | null;
  fva_m_lag?: number | null;
  fva_correction?: number | null;
  fva_state?: 'ok' | 'frozen' | 'not_applicable' | 'degraded' | null;
  fva_trigger_lo_bps?: number | null;
  fva_trigger_hi_bps?: number | null;
  // Publish-only shadow (BGA "gap_correct" since 2026-08-18; JH
  // "gap_correct_ramp" off the step-aside s_on since 2026-08-27). Computed
  // from the same live inputs but NEVER applied to the signal. JH's shadow
  // state mirrors the step-aside state ('live' pre-freeze).
  fva_shadow_m_t?: number | null;
  fva_shadow_correction?: number | null;
  fva_shadow_state?: 'ok' | 'live' | 'frozen' | 'not_applicable' | 'degraded' | null;
}

export interface BgaSignalData extends BaseSignalData {
  fund: 'BGA';
  kt_synth_nav_return_gbp: number | null;
  synth_nav_source: 'kt' | 'aggregate' | null;
  news_basket_sentiment: number | null;
  news_basket_coverage_pct: number | null;
  news_sentiment_filter: boolean | null;
  news_sentiment_threshold: number | null;
  news_sentiment_min_coverage_pct: number | null;
  news_sentiment_blocks_buy: boolean | null;
  imr_wz: number | null;
  imr_window: number | null;
  vix_mom_gate: string | null;
  wz_threshold: number | null;
  nas100_morning_anchor: number | null;
}

export interface JhSignalData extends BaseSignalData {
  fund: 'JH';
  nas100_morning_anchor: number | null;
}

export type SignalData = BgaSignalData | JhSignalData;

export interface PriceData {
  symbol?: string;
  bid: number;
  ask: number;
  mid: number;
  timestamp?: string;
  // Task 10 (gold live K_t plan, spec D6): Databento EQUS.MINI overlay
  // entries (gold-fund miners not on the Pepperstone map) carry these
  // instead of symbol/timestamp — bid=ask=mid since Databento snapshots
  // are a single 1m-bar close, not a two-sided quote.
  source?: 'pepperstone' | 'databento';
  tsUtc?: string;
}

export interface LastRebalanceInfo {
  detectedDate: string;
  added: string[];
  removed: string[];
}

export interface HoldingsStatusData {
  available?: boolean;
  lastUpdateDate: string;
  success: boolean;
  holdingsCount: number;
  totalWeightPct: number;
  warnings: string[];
  error: string | null;
  lastRebalance?: LastRebalanceInfo | null;
}

export interface SynthNavData {
  available?: boolean;
  timestamp: string;
  overnight_return: number | null;
  coverage_pct: number;
  holdings_count: number;
  observable_count: number;
}

// GET /api/holdings/active?fund={bga,jh,gge} — thin passthrough of
// holdings:{fund}:active. `weight`/`weightPct` naming is inconsistent
// across the fund publishers (historical — see HoldingsPanel.vue's `holdings`
// ref fallback branch), so both are optional here; callers null-coalesce.
// `currency`/`venue` (GGE enriched-parity arc, PR-A #451): venue resolved
// server-side via GuinnessEnergyTickerResolver against
// Config/guinness-energy-ticker-map.yaml — `NYSE | NASDAQ | OTC | HKEX |
// UNKNOWN`, never omitted (an unmapped ticker publishes "UNKNOWN", not a
// missing key). currency defaults to "USD" server-side when absent. Both
// null-safe optional here since old BGA/JH publishers never emit them.
export interface HoldingsActiveEntry {
  ticker: string;
  name?: string | null;
  weight?: number | null;
  weightPct?: number | null;
  currency?: string | null;
  venue?: string | null;
}

export interface EnrichedHoldingPrice {
  ticker: string;
  name: string;
  weight: number;
  prev_close: number | null;
  prev_close_t1: number | null;
  after_hours_close: number | null;
  nq_ref_at_fetch: number | null;
  proxy_price: number | null;
  return_pct: number | null;
  contribution_pct: number | null;
  spread_bps: number;
  source: string;
  last_trade_time: string | null;
  imr_zscore: number | null;
  // When imr_zscore is null for a basket member, this records why.
  // "engine_unavailable" | "no_live_price" | "warmup_incomplete" | null.
  imr_excluded_reason: string | null;
  // Gold telemetry only (2026-08-16): last completed session's return in the
  // holding's native ccy — the panel's weekend RET% fallback. Absent on BGA/JH.
  prev_return_pct?: number | null;
  tier: string | null;
  // Last two completed US session closes (holdings_daily CSVs). Outside RTH
  // the mkt pill shows session_close/session_prev_close - 1 (completed
  // session change, TradingView convention) instead of the beta-scaled
  // proxy return, which misses idiosyncratic moves (2026-07-13 GH:
  // stock -0.96%, proxy pill -0.21%). Optional: absent on old publishers.
  session_close?: number | null;
  session_prev_close?: number | null;
  // Stale session-anchor gate (2026-09-24, paolo): the calendar date the two
  // anchors above were read from. The panel compares it against the hybrid
  // row's lastRealAtUtc (as an ET session date) to detect anchors that a
  // whole session has since overtaken — at which point cur/session_close - 1
  // is not after-hours drift and the decomposition must be suppressed.
  // as_of cannot stand in for this: it is the MAX close date across every
  // holding, so it reads fresh off one venue while another sits behind.
  session_close_date?: string | null;
  // GROSS GBP-converted T/T-1 return per holding. Originally gold-only
  // (NAV-share column arc, Task 1 2026-09-05: gold_kt_series.compute_kt_return's
  // per_ticker_r_gbp, threaded through gold_instrument_mr.build_payload).
  // GGE parity (enriched-parity arc, PR-B #451): GgeHoldingsBackfillHostedService.
  // ComputeReturnPctGbp populates the same field on synth_nav:gge:enriched —
  // HKD holdings use the exact same GBPUSD-only formula as USD (the 7.80 peg
  // cancels out algebraically, see hkd_peg_assumption below). GROSS means NOT
  // renormalised by covered_weight_frac and NOT net of daily_drag_frac — the
  // client applies both when computing the NAV-share cell. Absent on BGA/JH
  // (and on old gold/GGE publishers pre-parity).
  return_pct_gbp?: number | null;
  // Truly-latest native-ccy close (may be a session newer than `prev_close`
  // if a rebalance shifted date alignment; usually equals `prev_close`).
  // Originally gold-only (fix/gold-latest-close-native, 2026-09-06: max date
  // in gold_instrument_mr's self._closes[ticker] — ignores the T-1 lag
  // convention `prev_close` honours for non-NY (LAG_CCYS) holdings). GGE
  // parity (PR-B #451): passthrough of GgeHoldingsBackfillHostedService's
  // lastClose. Used as the RET% cascade's fallback for holdings the
  // holdings-hybrid publisher doesn't cover, so non-NY holdings show the
  // real latest session move instead of a stale Wed→Thu return. Absent on
  // BGA/JH and on old gold/GGE publishers.
  latest_close_native?: number | null;
}

export interface EnrichedSynthNavData {
  available?: boolean;
  fund?: 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'OTP';
  // Gold telemetry only (2026-08-15 spec): hybrid-cadence freshness counter —
  // days since the 07:45 UK daily job last successfully computed betas/z.
  // Absent on BGA/JH publishers.
  stale_days?: number;
  // Gold telemetry only (2026-08-15 spec): the UK calendar date the daily
  // job computed betas/z for (`synth_nav:gold:enriched` §"as_of"). Drives
  // the `gold-enriched-freshness` integrity check. Absent on BGA/JH.
  as_of?: string;
  timestamp: string;
  overnight_return_usd: number | null;
  overnight_return_gbp: number | null;
  // 2026-05-21 (Bug 6): basket-frame USD preserved separately when the K_t
  // adapter overrides overnight_return_usd with a K_t-frame value. Null when
  // K_t override inactive — fall back to overnight_return_usd for the
  // contribution-rollup invariant check.
  overnight_return_usd_basket?: number | null;
  // Snapshot returns captured at the 10AM rebase. Null pre-rebase. Post-rebase
  // these are the totals that were "frozen" at 10:00 BST; overnight_return_*
  // above resets to ~0 and grows as drift since 10AM strike accumulates.
  snapshot_overnight_return_usd: number | null;
  snapshot_overnight_return_gbp: number | null;
  /** UK date the snapshot belongs to (the strike/NAV date it estimates). */
  snapshot_strike_date?: string | null;
  /** GOLD/GGE only (#767): which estimate the snapshot is — live K_t before the
   *  noon strike, the booked strike after it, the official print once landed. */
  snapshot_source?: 'kt_live' | 'strike_booked' | 'official' | null;
  coverage_pct: number;
  holdings_count: number;
  observable_count: number;
  nq_price: number | null;
  nq_nav_prev_close: number | null;
  nas100_prev_close_t1: number | null;
  // True while the US regular session is open (publisher-side
  // market_session.is_us_session_active). Optional: absent on old publishers.
  us_rth_open?: boolean | null;
  // 10:00 UK BGA-strike anchor; see SignalPayload.nas100_morning_anchor.
  nas100_morning_anchor: number | null;
  nq_10am: number | null;
  nq_spread_bps: number | null;
  gbpusd_rate: number | null;
  gbpusd_ref: number | null;
  gbpusd_ref_source: string | null;
  gbpusd_ref_date: string | null;
  gbpusd_ref_stale: boolean | null;
  mr_zscore: number | null;
  mr_beta: number | null;
  mr_tilt_bps: number | null;
  mr_tilt_direction: string | null;
  prev_official_nav_gbp: number | null;
  prev_official_nav_date: string | null;
  today_official_nav_gbp: number | null;
  today_official_nav_date: string | null;
  // Strike-freeze signal for the P&L pane (2026-09-21 paolo defect). True
  // iff a SELL trade has been booked for today's UK date with dealing_nav
  // still NULL — i.e. the position is pending its noon strike. Only
  // populated for GOLD/GGE; false/absent on BGA/JH. Consumed by
  // `computeGoldTodaysMovePct` to switch from live per-holding pricing to the
  // strike-frozen snapshot published by GoldHoldingsHybridService.
  sell_pending_at_strike?: boolean;
  imr_weighted_zscore: number | null;
  imr_breadth_cheap: number | null;
  imr_breadth_rich: number | null;
  imr_window: number | null;
  imr_momentum_count: number;
  nav_baseline_phase: string | null;
  nav_rebased_at: string | null;
  imr_as_of: string | null;
  holdings_detail: EnrichedHoldingPrice[];
  // BGA annual XD correction (D3 Track 3). Non-zero on XD day (~+130 bps) and
  // XD+1 (~-160 bps); null on regular days.
  xd_correction_applied?: boolean;
  xd_correction_bps?: number | null;
  xd_correction_label?: string | null;
  // The reconciling scalars for the identity
  // `overnight_return_gbp = (Σ weight × return_pct_gbp) / covered_weight_frac
  // - daily_drag_frac`. Originally gold-only (NAV-share column arc, Task 1
  // 2026-09-05): daily_drag_frac is the TER/dividend-leakage drag fraction
  // fed into compute_kt_return (a fixed 0.000325 constant in
  // gold_instrument_mr.py); covered_weight_frac is KtDay.covered from the
  // same call. GGE parity (PR-B #451): GgeHoldingsBackfillHostedService
  // emits the same two fields — GgeDailyDragFrac (0.000027, a shim pending
  // identity-residual calibration, see spec §7A.3/§12.4) and the running
  // sum of weight over holdings with a non-null return_pct_gbp. Both always
  // present in production for gold/GGE when each fund's extended-payload
  // kill switch is on; absent on BGA/JH and when the kill switch is off.
  daily_drag_frac?: number | null;
  covered_weight_frac?: number | null;
  // GGE parity only (PR-B #451): fixed `7.80` (GBPUSD peg) when the day's
  // basket contains an HKD holding, else `null`. Pure audit-trail field —
  // never enters the return_pct_gbp arithmetic (the peg cancels out
  // algebraically, see ComputeReturnPctGbp). Absent on BGA/JH/GOLD.
  hkd_peg_assumption?: number | null;
}

export interface EquityAvailabilityEntry {
  ticker: string;
  pepperstoneName: string;
}

export interface EquityPricesResponse {
  available: boolean;
  equities: EquityAvailabilityEntry[];
  prices: Record<string, PriceData>;
}

export interface EventEntry {
  timestamp: string;
  type: string;
  message: string;
  detail: string | null;
}
// DevOps tab (spec 2026-10-02): GET /api/devops/findings. `detail` is null unless
// the caller SeesAdminData — the server strips it, the client never filters.
export interface AgentFindingDetail {
  rootCause: string | null;
  evidence: string[];
  recommendedAction: string | null;
  confidence: string | null;
  runbookRef: string | null;
  benignMatch: string | null;
}
// Operator acknowledgement (Paolo 2026-10-16): "seen, handled". `byEmail`, `note` and
// `reference` are null for viewers without admin data; `at` always survives. The agent's
// own status is never rewritten — the mark sits beside it and lapses when the agent
// produces a newer verdict for that day.
export interface AgentFindingAcknowledgement {
  byEmail: string | null;
  at: string;
  note: string | null;
  reference: string | null;
}
export interface AgentFindingDay {
  date: string;              // YYYY-MM-DD (UTC run day)
  status: string;            // HEALTHY | BENIGN_KNOWN | DEGRADED | INCIDENT (agent-owned, may grow)
  affectedModule: string | null;
  runs: number;
  lastRunUtc: string | null;
  model: string | null;
  detail: AgentFindingDetail | null;
  acknowledgement?: AgentFindingAcknowledgement | null;
}
export interface AgentFindingAckResponse { agent: string; day: AgentFindingDay | null }
export interface AgentFindings {
  agent: string;             // 'platform' | 'strategy'
  label: string;
  available: boolean;
  reason: string | null;
  days: AgentFindingDay[];   // newest first, ≤ 4 (latest + 3 history)
}
export interface DevOpsFindingsResponse { agents: AgentFindings[] }


export interface JhNavRow { date: string; navPence: number }

export interface JhNavResponse {
  latest: JhNavRow;
  prev: JhNavRow;
  returnPct: number;
  sparkline: JhNavRow[];
}

export interface JhNavHistoryRow {
  date: string;
  jhNavGbp: number;
  nas100Usd: number;
  nas100Gbp: number | null;
  bgaNavGbp?: number | null;
}

export interface JhNavHistoryResponse {
  window: '30d' | '90d' | '1y' | '5y' | 'all';
  ccyAdjusted: boolean;
  compare?: 'bga' | null;
  rows: JhNavHistoryRow[];
}

export interface AccountValuation {
  accountId: number;
  name: string;
  totalValue: number;
  availableCash: number;
}

export interface UserAccountAllocation {
  userId: string;
  accountId: number;
  accountName: string;
  allocatedAmount: number;
  updatedAt: string;
}

export interface SaveAllocationRequest {
  accountId: number;
  accountName: string;
  allocatedAmount: number;
}

// OTP/BGA divorce Phase 1, Task 3+4: otpReservePct/otpAccountId retired from
// the execution-config wire shape — the server's GET no longer returns them
// (UserExecConfigResponse) and the save endpoint ignores them if a stale
// client still sends them. ExecPanel's OTP Reserve/Account dropdowns are
// gone; residual-funded status now comes from OtpResidualStatus below.
// cutoffTimeUtc retired 2026-09-30: auto-execute is ON/OFF; BGA deals at
// 08:41 UK after the dealing plan publishes.
export interface UserExecutionConfig {
  userId: string;
  autoExecute: boolean;
  maxRetries: number;
  compoundOtp: boolean;
  /** Per-account plan flag (config:rebalance:plan.per_account) state. Optional so older fixtures compile; absent = off. */
  perAccountPlan?: boolean;
  /** True when the user is on the live shared pot (shared_pot_v1, not shadow): Auto-compound then
   *  sizes the pot (ON = whole pot, OFF = cash capped at each allocation). Absent = false. */
  sharedPot?: boolean;
}

export interface UserExecConfigRequest {
  autoExecute: boolean;
  maxRetries: number;
  // Omitted = keep the stored value. Auto-compound is owned by the Account
  // panel (userSetAutoCompound) since 2026-09-27.
  compoundOtp?: boolean;
}

// OTP/BGA divorce Phase 1, Task 4: wire shape for GET /api/exec/otp-residual
// — read-only view over DealingPlanTickService's post-cutoff residual-cash
// Redis key, backing ExecPanel's "OTP sleeve — residual-funded" row.
export interface OtpResidualAccountStatus {
  accountId: number;
  residualGbp: number;
}

export interface OtpResidualStatus {
  totalGbp: number;
  byAccount: OtpResidualAccountStatus[];
}

// OTP standalone Plan C, Task 2 (2026-08-30-otp-standalone-plan-c-web): wire
// shape for GET /api/otp/gate — mirrors OtpGateService.OtpGateState's
// camelCase JSON (date/status/residualTotalGbp/residualRemainingGbp).
export interface OtpGateView {
  date: string;
  status: string;
  residualTotalGbp: number;
  residualRemainingGbp: number;
}

// Task 3: wire shape for GET/PUT /api/otp/registry rows (Plan B's
// UserOtpConfigRepository projection — camelCase, see Program.cs's
// api.MapGet("/otp/registry", ...) row-select).
export interface OtpRegistryRow {
  otpType: string;
  enabled: boolean;
  targetPct: number;
  sizePct: number;
  stopLossPct: number;
}

export interface UserOtpConfig {
  userId: string;
  otpType: string;
  enabled: boolean;
  triggerType: string;
  triggerThreshold: number;
  sizeRatio: number;
  sizePct: number;
  leverageFactor: number;
  spreadBps: number;
  annualTer: number;
  dealingChargeGbp: number;
  cashBufferPct: number;
  useTermStructure: boolean;
  stopLossPct: number;
  cooldownEnabled: boolean;
  cooldownDays: number;
}

export interface VixIntraday {
  available?: boolean;
  date?: string;
  open?: number;
  current?: number;
  change_pct?: number;
  /** UTC timestamp of the latest tick. Updates every few seconds. */
  captured_at?: string;
  /** UTC timestamp of when `open` was actually captured (frozen at session
   * open ~08:00 UK). Distinct from `captured_at`. Used by the integrity
   * panel to detect "open re-captured mid-session" (restart rebase smell). */
  open_captured_at?: string;
}

// AlphaVantage daily-endpoint health snapshot. Mirrors the `av:health:latest`
// payload written by AlphaVantageHealthService every 6h. The integrity-panel
// check `fresh-alphavantage-daily` consumes this to flag a silent AV outage
// within hours, not weeks (2026-05-12 incident: AV daily-data pipeline
// silently produced 0 new rows for 26 days; UI showed DDOG +62% as a result).
export interface AlphaVantageHealthSentinel {
  symbol: string;
  latestRow: string | null;       // YYYY-MM-DD
  status: 'ok' | 'stale' | 'rate_limit' | 'http_error';
  details: string;
}

export interface AlphaVantageHealth {
  available?: boolean;            // false when server returns the no-probe-yet envelope
  reason?: string;                // populated when available === false
  checkedAt?: string;             // ISO UTC timestamp
  status?: 'ok' | 'stale' | 'rate_limit' | 'http_error';
  latestRowDate?: string | null;
  lagDays?: number | null;
  sentinels?: AlphaVantageHealthSentinel[];
  details?: string;
}

// BGA trade FIFO-coherence snapshot (2026-06-01). Backend reads the
// per-user `trades` table and walks the running balance
// Σ(BUY units) − Σ(SELL units) over (date, trade_id). Drives the
// `cohere-bga-no-orphan-sell-units` probe — surfaces the defensive
// follow-up from the 2026-06-01 OTP win-rate audit
// (`docs/analysis/2026-06-01-otp-cycle-extractor-roundtrip-drift.md`
// §"Audit next"): `BgaTradeCyclePairer.Pair()` at L137-138 silently
// drops over-SELL units, so a corrupted history would vanish from
// win-rate accounting without an explicit alarm.
export interface BgaTradeCoherence {
  available: boolean;
  minRunningPosition: number;
  maxRunningPosition: number;
  violationCount: number;
  lastViolationDate: string | null;
  totalBuyUnits: number;
  totalSellUnits: number;
  orphanSellUnits: number;
}

// BG holdings effective-date snapshot (2026-05-24). Backend reads
// `holdings:bg:effective` from Redis (written by HoldingsUpdateService
// on every successful BG fetch). Drives `fresh-bg-effective-date` —
// surfaces the upstream "BG file frozen across quarters" class (latent
// follow-up b on docs/analysis/2026-05-22-holdings-snapshot-date-by-
// save-time.md).
export interface BgEffectiveDateHealth {
  available: boolean;             // false when no successful fetch since startup
  reason?: string;
  checkedAt?: string;             // ISO UTC
  effectiveDate?: string | null;  // YYYY-MM-DD = BG's Excel "as at" header
  fetchedAt?: string | null;      // ISO UTC of the successful fetch
  effectiveDateAgeDays?: number | null;  // calendar days now − effectiveDate
  fetchedAtAgeHours?: number | null;     // wall hours since the fetch
}

// Databento equity-feed health snapshot. Computed live on every GET (no
// hosted-service writer like AV) — endpoint walks the top-5 basket tickers
// and reports max age across them. Surfaces the 2026-05-13/14 incident class
// (SDK missing → keys freeze → synth NAV runs on stale extrapolation).
//
// The endpoint always returns the raw numbers; the integrity check applies
// the severity rule (>5min during US RTH = FAIL; outside session = PASS,
// since pre-open keys legitimately go cold overnight).
export interface DatabentoEquityProbedTicker {
  ticker: string;
  ageSeconds: number;
  tsUtc: string;                  // ISO UTC of the price's ts_utc
}

// K_t intraday live-adapter health (2026-05-17). Composed live by the
// /api/integrity/kt-intraday-live endpoint from latest:kt_intraday +
// kt:morning_anchor:{date} + config:override:synth_nav_source + latest:signal.
// Lets the operator spot stalls in any link of the K_t → synth-NAV chain.
export interface KtIntradayLiveHealth {
  available: boolean;
  reason?: string;
  checkedAt?: string;
  inUsSession?: boolean;
  // Day-only counterpart of inUsSession (added 2026-05-24). True Mon-Fri
  // non-NYSE-holiday irrespective of wall-clock; lets the frontend gate
  // "non-trading day" branches without recomputing the NYSE calendar in TS.
  isUsTradingDay?: boolean;
  today?: string;
  latestKtIntraday?: {
    present: boolean;
    tsUtc: string | null;
    ageSeconds: number | null;
    kValue: number | null;
    // 2026-05-28: row-level basket coverage. When the K_t row is stale AND
    // coveredWeightPct is low, the writer's 5-min maturity-gate fallback
    // already fired on a low-coverage minute (structural during US after-
    // hours when basket bars stop flowing). Null on pre-2026-05-28 API.
    coveredWeightPct?: number | null;
    contributingTickers?: number | null;
  };
  morningAnchor?: {
    present: boolean;
    kValue: number | null;
    capturedAt: string | null;
  };
  configOverride?: {
    requestedSource: string | null;        // "kt" / null / other
    activeSource: string | null;            // what signal_service actually published
    consistent: boolean;                    // requested == active
  };
  liveReturns?: {
    ktReturnGbp: number | null;
    legacyReturnGbp: number | null;
    divergenceBps: number | null;           // (kt - legacy) × 10000
  };
  // 2026-05-17 monitoring deep-dive — these three are publishd by separate
  // writers (Postgres repo, C# writer, Python adapter) so a single endpoint
  // surfaces the full chain in one fetch.
  postgres?: {
    latestTsUtc: string | null;             // MAX(ts_utc) from basket_kt_intraday
    ageSeconds: number | null;              // now − latestTsUtc
    redisVsPgLagSeconds: number | null;     // pg − redis; >0 means Redis publish lagging
  };
  gateTrips?: {
    windowSeconds: number;
    evaluatedBars: number | null;
    skippedBars: number | null;
    tripPct: number | null;                 // 100 × skipped / evaluated
    lastTripAt: string | null;
  };
  // RTH basket-coverage cohere metric (2026-05-23). Post-maturity-gate fix
  // a row whose covered_weight_pct < thresholdPct means the writer's 5-min
  // fallback fired — indicates a real Databento publish gap, not the
  // writer-cadence retries that gateTrips tracks.
  basketCoverage?: {
    windowSeconds: number;
    thresholdPct: number;                   // covered_weight < this = low
    evaluatedMinutes: number | null;
    lowCoverageMinutes: number | null;
    lowPct: number | null;                  // 100 × low / evaluated
    lowestCoveragePct: number | null;
  };
  adapterFallback?: {
    windowSeconds: number;
    eventCount: number | null;
    lastEventAt: string | null;
  };
  databentoLive?: {
    present: boolean;
    tsUtc: string | null;
    ageSeconds: number | null;
  };
  nas100Live?: {
    present: boolean;
    tsUtc: string | null;
    ageSeconds: number | null;
  };
  // 2026-05-26 — per-consumer halt history (signal_service's auto-recovery
  // wrapper publishes here on every ReconnectStormHaltError). Drives the
  // `fresh-{databento|nas100}-live-halt-history` tiles which fire regardless
  // of inUsSession — closes the 2026-05-24 → 2026-05-26 silent-49h-death
  // class that the existing fresh-databento-live-bars tile missed (it only
  // escalates during US RTH so weekend/holiday halts stayed invisible).
  databentoLiveHaltHistory?: {
    lastHaltAt: string | null;       // ISO UTC
    haltCount24h: number;
    lastReason: string | null;
  };
  nas100LiveHaltHistory?: {
    lastHaltAt: string | null;
    haltCount24h: number;
    lastReason: string | null;
  };
}

export interface DatabentoEquityHealth {
  available: boolean;             // false on probe-failure envelope
  reason?: string;
  checkedAt?: string;
  basketSourceMode?: 'primary' | 'fallback';
  inUsSession?: boolean;          // 13:30-21:00 UTC = US RTH (NYSE)
  // Day-only counterpart (added 2026-05-24). Mon-Fri non-NYSE-holiday.
  isUsTradingDay?: boolean;
  maxAgeSeconds?: number;         // -1 when no tickers probed successfully
  oldestTicker?: string | null;
  probed?: DatabentoEquityProbedTicker[];
  missingTickers?: string[];      // had no databento:price entry, or parse error
  // Pepperstone-auto-fallback telemetry (2026-05-15). Count of fallback
  // firings observed in the trailing 15 min by signal_service. Zero = healthy
  // primary feed; small N = transient glitches auto-mitigated; large N =
  // real Databento problem worth investigating. Null when telemetry hasn't
  // been published yet (e.g. first 30s after signal_service startup).
  fallbackEventsLast15Min?: number | null;
  // 2026-05-28: distinct ticker count is the better outage signal —
  // low-volume basket names naturally flap stale-then-recover, inflating
  // event_count. Distinct-ticker count separates "broad Databento gap"
  // from "name-specific low-volume flap". Null when telemetry hasn't
  // been published yet OR on pre-2026-05-28 signal_service builds.
  fallbackDistinctTickersLast15Min?: number | null;
  fallbackLastEventAt?: string | null;  // ISO UTC, null if no recent events
}

// Local-CSV freshness snapshot from /api/integrity/data-csv-freshness.
// Returns the max date inside each monitored data-directory CSV. Initial
// coverage is vix_ohlc.csv only; future expansion per
// project_signal_panel_daily_csv_gap_check.md extends the file list at
// the backend. Distinct from AlphaVantageHealth: AV health probes the
// upstream API; this probes the local post-ingest CSV result.
export interface DataCsvFreshnessFile {
  name: string;                   // filename relative to data/
  label: string;                  // human-readable
  consumers: string;              // who reads this file (for operator context)
  latestDate: string | null;      // YYYY-MM-DD; null if file empty/missing/unparseable
  // Largest calendar-day gap between any two consecutive rows in the last
  // ~30 dated rows. Catches the AAPL-style partial-backfill failure mode
  // where a recent row is inserted on top of an earlier 26-day hole and
  // a max-date-only check sees the file as "fresh". Null when fewer than
  // 2 parseable rows present. Optional in the type so older fixtures /
  // callers that predate Phase 2 of the gap-check work still compile.
  maxInternalGapDays?: number | null;
  // Count of rows whose date appears more than once. 0 = clean; >0 = a
  // multi-writer race shipped a duplicate (the 2026-05-16 nas100.csv
  // incident that silently crashed IMR warm-up). Null when the file is
  // missing or has no parseable rows. Optional so older fixtures compile.
  duplicateDateCount?: number | null;
  exists: boolean;
}

export interface DataCsvFreshnessBasket {
  // Tickers selected from `holdings:bga:active` by weight (top-N). Listed
  // so the frontend can render which holdings are inside the aggregate.
  tickers: string[];
  files: DataCsvFreshnessFile[];
  // Gold basket only: names the ticker map retired (route unsourced / delisted)
  // — left out of `files`.
  retiredTickers?: string[];
  // Gold basket only: suffixed ticker → its listing exchange's recent closure
  // dates (Config/exchange_holidays.json). Unsuffixed (US) tickers are absent.
  venueClosures?: Record<string, string[]>;
}

// NAV cross-check surface from /api/integrity/nav-cross-checks
// (2026-08-06 gold-decision-inputs monitoring). Each slot is the raw JSON
// string the fund's NAV poller wrote to nav:{fund}:cross_check — an
// observation ({date, price, source}) or, gold since the 08-05
// share-class swap, a disabled marker ({status:"disabled", reason}).
// Null = key absent (120h TTL expired / poller not running) or Redis down;
// the frontend owns parse + severity so backend stays a dumb pipe.
export interface NavCrossChecks {
  bga: string | null;
  jh: string | null;
  gold: string | null;
  redisAvailable: boolean;
}

// Postgres-backed intraday table probe (not a CSV). Backend queries
// MAX(ts_utc) and COUNT(*) on the named table. ageMinutes is computed
// at probe time so the frontend doesn't have to re-derive clock skew.
export interface DataCsvFreshnessIntradayTable {
  table: string;            // e.g. "gold_intraday_1m"
  label: string;            // human-readable
  consumers: string;
  latestTsUtc: string;      // ISO UTC of MAX(ts_utc)
  ageMinutes: number;       // age at probe time (UTC now - latestTsUtc)
  rowCount: number;         // sanity check — backfill ≥0, full history ~M+
}

// IMR warm-up status snapshot (2026-05-18). Published by signal_service
// on every startup. ``available=false`` when no warmup has been recorded
// yet (fresh deploy / Redis flushed). When available, ``status`` is one of
// "success" | "failed"; "starting" should be transient — observing it for
// more than a few seconds means the warm-up never completed.
export interface ImrWarmupHealth {
  available?: boolean;
  reason?: string;
  timestamp?: string;             // ISO UTC of last completion
  status?: 'success' | 'failed' | 'starting';
  days?: number;
  tickers?: number;
  composite_available?: boolean;
  composite_date?: string | null;
  error?: string | null;          // populated when status === "failed"
}

// RotationTiltStatus — REMOVED 2026-09-15 with the rotation tilt
// decommission (endpoint /api/integrity/rotation-tilt-status deleted).

export interface DataCsvFreshness {
  checkedAt: string;              // ISO UTC timestamp
  files: DataCsvFreshnessFile[];
  // Top-N basket holdings (per-ticker daily-close CSVs). Null when the
  // backend can't reach Redis or `holdings:bga:active` is missing/malformed.
  // Optional in the type so older fixtures / consumers compile.
  basket?: DataCsvFreshnessBasket | null;
  // Gold-basket holdings (every CSV under data/gold_holdings_daily/).
  // Operator-drift catch-net for the manual scripts/fetch_gold_prices.py
  // refresh — surfaces silently-stale per-ticker history before it breaks
  // the gold synth-NAV pipeline. Null when the directory doesn't exist
  // yet (pre-first-fetch state).
  goldBasket?: DataCsvFreshnessBasket | null;
  // Gold 1m intraday — Postgres table gold_intraday_1m, refreshed every
  // 15min by signal_service _gold_intraday_writer_loop. Null when the
  // table is missing or empty (pre-first-backfill state) — UI warns
  // "probe not available" then.
  goldIntraday?: DataCsvFreshnessIntradayTable | null;
  // Treasury 1m intraday — Postgres table treasury_intraday_1m (ZN.n.0
  // 10y notes from Databento). Consumer: Treasury → gold OTP overlay
  // backtest. Refreshed every 15min by signal_service
  // _treasury_intraday_writer_loop on weekdays.
  treasuryIntraday?: DataCsvFreshnessIntradayTable | null;
}

export interface BgaCusumParams {
  sigmaSpan: number;
  kH: number;
  kUp: number;
  kDn: number;
  horizonDays: number;
}

export interface BgaCusumSeriesPoint {
  date: string;
  nav: number;
  logRet: number | null;
  sigmaT: number | null;
  stPlus: number | null;
  stMinus: number | null;
  hUpper: number | null;
  hLower: number | null;
}

export interface BgaCusumEvent {
  eventDate: string;
  eventPrice: number;
  sigmaAtEvent: number;
  upperPrice: number;
  lowerPrice: number;
  verticalDate: string;
  exitDate: string | null;
  exitPrice: number | null;
  label: 1 | -1 | 0 | null;
}

export interface BgaCusumResponse {
  params: BgaCusumParams;
  series: BgaCusumSeriesPoint[];
  events: BgaCusumEvent[];
}

// GET /api/user/exec/decisions (2026-10-02): the Exec screen's household-wide
// "Signal at Decision Time" — one row per fund the caller may access. Null
// fields = no decision logged yet today. signalValue is in signalUnit: '%' for
// BGA/JH (synth-NAV move) and JAPAN (08:40 m), 'bp' for GOLD/GGE (08:40 m).
export interface ExecFundDecision {
  fund: PotFund;
  decision: string | null;
  signalValue: number | null;
  signalUnit: '%' | 'bp' | null;
  note: string | null;
  decidedAtUtc: string | null;
}

export interface UserExecutionStatus {
  userId: string;
  // Multi-strategy allocation (V065): user_execution_status is now
  // fund-scoped (PK (user_id, fund_id)) — the server always returns this,
  // but keep it optional so pre-existing test fixtures without the field
  // still satisfy the type.
  fundId?: string;
  status: string;
  decision: string | null;
  signalValue: number | null;
  executedAt: string | null;
  error: string | null;
  updatedAt: string;
}

// Backtest types
export interface HedgeParams {
  triggerType: 'delta' | 'absolute' | 'percentage' | 'regime';
  triggerThreshold: number;
  hedgeSizeRatio: number;
  spreadBps: number;
  annualTer: number;
  leverageFactor: number;
  bgaBeta: number;
  cashBufferPct: number;
  dealingChargeGbp: number;
  overnightRetMinPct?: number | null;  // standalone OTP gate: fire only if NQ 08:40 ret (%) ≥ this
  fireWindowEndHourUk?: number | null; // no fires at/after this UK hour; null = whole session
}

export interface OverlayParams {
  triggerThreshold: number;
  overlaySizePct: number;
  spreadBps: number;
  annualTer: number;
  leverageFactor: number;
  dealingChargeGbp: number;
  overnightRetMaxPct?: number | null;  // standalone OTP gate: fire only if NQ 08:40 ret (%) ≤ this
  fireWindowEndHourUk?: number | null; // no fires at/after this UK hour; null = whole session
}

export interface BacktestParams {
  zWindow: number;
  tiltBps: number;
  vixScale: number;
  termStructure: boolean;
  nfpFilter: boolean;
  holidayFilter: boolean;
  opexFilter: boolean;
  quadWitchingFilter: boolean;
  exDividendFilter: boolean;
  futuresRollFilter: boolean;
  noHold: boolean;
  imrMode: string;
  imrTiltBps: number;
  imrWindow: number;
  imrBreadthThreshold: number;
  vixMomGate: string;       // "off" | "regime" | "term" | "both" | "circuit"
  autoOptimize: boolean;
  eomInterestFilter: boolean;
  eomInterestBoostBps: number;
  useHedging: boolean;
  hedge?: HedgeParams;
  useOverlay: boolean;
  overlay?: OverlayParams;
  useShortConviction: boolean;
  shortConviction?: HedgeParams;
  useLongConviction: boolean;
  longConviction?: {
    triggerThreshold: number;
    overlaySizePct: number;
    spreadBps: number;
    annualTer: number;
    leverageFactor: number;
    dealingChargeGbp: number;
    overnightRetMaxPct?: number | null;  // standalone OTP gate: fire only if NQ 08:40 ret (%) ≤ this
    fireWindowEndHourUk?: number | null; // no fires at/after this UK hour; null = whole session
  };
  // Portfolio-wide OTP reserve (mirrors user_execution_config.otp_reserve_pct).
  // Applies whenever any OTP strategy is on. null = "Sweep (auto)" (grid
  // searches the reserve axis under auto-optimize); 0 = "NO OTP" (pinned
  // fully-invested, never swept). Optional for backcompat with saved
  // configs that only have hedge.cashBufferPct.
  otpReservePct?: number | null;
  /** @deprecated engine-only; no UI control — field preserved so full-file preset saves round-trip it */
  applyRecalibrationBias?: boolean;
  /** @deprecated engine-only; no UI control — field preserved so full-file preset saves round-trip it */
  recalibrationWindowDays?: number;
  // IMR mom-gate dead-zone (2026-04-28). Widens the wz threshold by this many
  // z-units to suppress noise-band flips. 0.10 in production (a-b commit 83fbce5).
  imrMomGateDeadZone?: number;
  // News sentiment v1 (2026-04-28). Filter blocks BUY when basket sentiment ≤
  // threshold AND coverage ≥ min — mirrors NewsSentimentAbBacktest. Default OFF.
  newsSentimentFilter?: boolean;
  newsSentimentThreshold?: number;
  newsSentimentMinCoveragePct?: number;
  newsSentimentMode?: string;          // "buy_only" | "both"
  newsSentimentUseZ?: boolean;
  newsSentimentZThreshold?: number;
  // Sampling mode (project_cusum_event_time_backtest_plan.md).
  // "daily" (default) = legacy per-trading-day iteration.
  // "event"           = iterate on CUSUM-event dates only (López ch. 2).
  // Optional + back-compat: presets without this field default to daily.
  sampling?: 'daily' | 'event';
  eventSamplingSigmaSpan?: number;
  eventSamplingKH?: number;
  /** CUSUM input column for event sampling: official (default) | kt_synth | fva_adjusted */
  eventSamplingNavSource?: 'official' | 'kt_synth' | 'fva_adjusted';
  eventSamplingKUp?: number;
  eventSamplingKDn?: number;
  eventSamplingHorizonDays?: number;
  // Canonical 09:45 synth-NAV signal source (surfaced 2026-07-14): which
  // canonical column the strategy consumes when useCanonicalSynthNav is on —
  // "aggregate" (live writer primary row) or "etf_trick" (K_t Method B).
  // Surfaced because the field previously round-tripped invisibly: a preset
  // saved/inserted without it silently took the C# default ("aggregate"),
  // which is how databento_kt_fva_tuned_v1 lost its TPE-tuned etf_trick.
  useCanonicalSynthNav?: boolean;
  synthNav945Method?: 'aggregate' | 'etf_trick';
  // NQ-overnight step-aside gate (JH probe 2026-07-23): "pure" = frozen
  // hold-unless-overnight-drag rule; "gate" = SELL-override on the normal
  // strategy. Threshold in bps (0 = step aside on any negative print).
  // Optional + back-compat: presets without these default to off/0.
  stepAsideMode?: 'off' | 'pure' | 'gate';
  stepAsideThresholdBps?: number;
  // 2026-09-15 BUY-side threshold for the dead-zone rule. Nullable:
  // null / undefined = legacy one-sided rule (no dead zone). A non-null
  // value must be >= stepAsideThresholdBps (API/panels refuse otherwise).
  stepAsideBuyThresholdBps?: number | null;
  // Hysteresis re-entry threshold: null/undefined = memoryless (re-enter as
  // soon as the print clears the exit threshold); set ≥ exit threshold to
  // stay flat until the print recovers to this level (turnover reduction).
  stepAsideReentryBps?: number | null;
  // Yield-collapse gate (gold asset profile, Task 6): "on" = SELL override
  // when the US 10y Treasury yield has dropped more than yieldCollapseDropBps
  // over the prior yieldCollapseWindowDays. Mirrors BacktestParams.cs
  // YieldCollapseGateMode/YieldCollapseDropBps/YieldCollapseWindowDays.
  // Optional + back-compat: presets without these default to off/50/20.
  yieldCollapseGateMode?: 'off' | 'on';
  yieldCollapseDropBps?: number;
  yieldCollapseWindowDays?: number;
  // Clamp the replay window to start at the first day with a step-aside
  // signal row (Databento intraday coverage start). Realistic-window flag:
  // pre-coverage days fail-safe to hold and drag full-period metrics
  // (gold 2008-2010: Sharpe 2.27 clean vs 1.77 full). No-op for assets
  // with no step-aside series (BGA). C#/wire default false; the Gold
  // panel defaults it ON.
  postDatabentoOnly?: boolean;
  // GGE window clamp: floors backtest days at 2019-08-01 (WS took over
  // management from Artemis 2019-07-31). No-op for non-GGE profiles.
  // GgeBacktestPanel defaults it ON; C# default false so BGA/JH/Gold
  // presets round-trip unchanged.
  guinnessEraOnly?: boolean;
  // Stand-alone OTP mode (OtpBacktestPanel, assetId 'otp'): mirrors
  // SideWinder.Backtest.Engine.OtpStandaloneParams. Required for assetId
  // 'otp' runs — BacktestJobService.SanitizeForAsset throws if this is null
  // on an 'otp' job. Standalone mode replaces the fund-dealing decision
  // gate with the day-type derived from the Nq0840OvernightRet print and
  // funds the enabled components from OtpResidualCashModel.ComputeDay
  // rather than an actual fund position. No-op for non-OTP profiles.
  otpStandalone?: OtpStandaloneParams;
  // Shadow-strategy preset marker + frozen-parameter capsule (Phase C.2 of
  // docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md). Non-null
  // on backtest_configs/otp.json shadow presets (cf_nq_shadow_v1,
  // vt_long_m5_shadow_v1, vt_long_m10_shadow_v1, gvs_shadow_v1,
  // oil_cf_shadow_v1, oil_vt_shadow_v1). Mirrors ShadowStrategyParams.cs.
  // OtpBacktestPanel's picker (Phase C.4) reads presets whose params carry
  // this sub-block and lets the user edit only the three cost inputs
  // (notionalGbp, spreadBps, dealingChargeGbpPerSide) — every other field
  // is frozen against the source study.
  shadowStrategy?: ShadowStrategyParams;
}

/// Shadow-strategy signal-family slugs. Mirrors ShadowSignalFamilies.All.
export type ShadowSignalFamily = 'contrarian_fade' | 'vt_long_faithful' | 'vt_spike_long';
export type ShadowUnderlyingInstrument = 'LQQ3' | '3LGO' | '3LOI';

export interface ShadowStrategyParams {
  frozen: boolean;
  signalFamily: ShadowSignalFamily | string;
  underlyingInstrument: ShadowUnderlyingInstrument | string;
  studyExpectedSharpe: number;
  sourceDoc: string;
  lookbackDays?: number | null;
  threshold?: number | null;
  overnightThreshold?: number | null;
  holdDays: number;
  leverageFactor: number;
  annualTer: number;
  // Editable-in-UI cost inputs (Phase C.4). Every field above is frozen
  // against the source study; changing one requires a fresh study pass
  // and a NEW _v2 preset.
  notionalGbp: number;
  spreadBps: number;
  dealingChargeGbpPerSide: number;
}

// Mirrors SideWinder.Backtest.Engine.OtpComponentKind — [JsonStringEnumConverter]
// serializes by member name, not a custom naming policy, so these three
// string literals are exact and case-sensitive on the wire.
export type OtpComponentKind = 'LongOverlay' | 'LongConviction' | 'ShortConviction';

export interface OtpComponentAllocation {
  kind: OtpComponentKind;
  targetPct: number;   // fraction of the day's residual, [0,1]
  enabled: boolean;
}

// Mirrors SideWinder.Backtest.Engine.OtpFundingMode ([JsonStringEnumConverter] → member names).
export type OtpFundingMode = 'Residual' | 'FixedNotional';

export interface OtpStandaloneParams {
  fundingMode?: OtpFundingMode;     // default Residual (C#); the panel sends FixedNotional
  fixedNotionalGbp?: number;        // FixedNotional pool per day (default 50000)
  portfolioNavGbp?: number;         // Residual NPV seed (default 50000)
  cashReservePct: number;           // fraction of NAV, [0,1)
  stepAsideSlicePct?: number;       // Task 9 makes the panel send this; optional until then — C# defaults 0.25
  /** @deprecated alias of stepAsideSlicePct kept for saved presets */
  bgaWeight?: number;
  allocations: OtpComponentAllocation[];
}

export interface BacktestProgress {
  currentFold: number;
  totalFolds: number;
  phase: string;
  percentComplete: number;
  completed?: number;
  total?: number;
  elapsedMs?: number;
}

export interface EquityPoint {
  date: string;
  npv: number;
}

export interface BacktestResultData {
  totalReturnPct: number;
  cagrPct: number;
  sharpeRatio: number;
  deflatedSharpeRatio: number;
  skewness: number;
  excessKurtosis: number;
  maxDrawdownPct: number;
  maxDrawdownDays: number;
  winRate: number;
  tradeCount: number;
  // Trade-cycle win rate (Phase 3, 2026-04-21): fraction of complete BUY→SELL
  // round trips with positive P&L, using FIFO partial-close pairing. This
  // matches the live Risk panel's trade-cycle WR so live and backtest are
  // directly comparable. See docs/analysis/2026-04-21-backtest-win-rate-analysis.md.
  cycleWinRate: number;
  cycleCount: number;
  buyAndHoldReturnPct: number;
  excessReturnPct: number;
  equityCurve: EquityPoint[];
  buyAndHoldCurve: EquityPoint[];
  strategyName: string;
  tradingDays: number;
  startDate: string;
  endDate: string;
  hedgeDaysCount: number;
  hedgeWinRate: number;
  hedgeTotalPnl: number;
  hedgeTotalCosts: number;
  unhedgedEquityCurve: EquityPoint[];
  overlayDaysCount: number;
  overlayWinRate: number;
  overlayTotalPnl: number;
  overlayTotalCosts: number;
  longConvictionDaysCount: number;
  longConvictionWinRate: number;
  longConvictionTotalPnl: number;
  longConvictionTotalCosts: number;
  shortConvictionDaysCount: number;
  shortConvictionWinRate: number;
  shortConvictionTotalPnl: number;
  shortConvictionTotalCosts: number;
}

export interface ComboScore {
  params: BacktestParams
  fitness: number
  sharpe: number
  deflatedSharpe: number
  cagr: number
  maxDrawdownPct: number
  winRate: number
  tradeCount: number
  /** Walk-forward grids only: fold test slices stitched (out-of-sample). Null elsewhere. */
  oosSharpe?: number | null
  oosCagr?: number | null
  oosDays?: number
}

export interface RankedResult {
  rank: number
  params: BacktestParams
  fitness: number
  fitnessBreakdown: Record<string, number>
  fullResult: BacktestResultData
  /** Walk-forward grids only: fold test slices stitched (out-of-sample). Null elsewhere. */
  oosSharpe?: number | null
  oosCagr?: number | null
  oosDays?: number
}

export interface GridSearchResult {
  topResults: RankedResult[]
  allScores: ComboScore[]
  totalCombos: number
  elapsedMs: number
  scoreType: 'sharpe' | 'fitness'
  /** Walk-forward grids (2026-10-08): each fold's train winner on its own next test window, stitched. */
  selectedOosSharpe?: number | null
  selectedOosCagr?: number | null
  selectedOosDays?: number
  selectedOosDistinctPicks?: number
}

export interface WalkForwardFoldData {
  fold: number;
  testStart: string;
  testEnd: string;
  trainSharpe: number;
  trainStart: string;
  trainEnd: string;
  trainScore?: number;
  testScore?: number;
}

export interface WalkForwardResultData {
  fixedResult: BacktestResultData;
  refitResult: BacktestResultData;
  inSampleResult: BacktestResultData;
  fixedFoldResults: BacktestResultData[];
  refitFoldResults: BacktestResultData[];
  cagrDecayFixed: number;
  sharpeDecayFixed: number;
  cagrDecayRefit: number;
  sharpeDecayRefit: number;
  folds: WalkForwardFoldData[];
  fixedParams?: BacktestParams;
  scoreType?: string;
  fixedGridResult?: GridSearchResult;
  meanOosScore?: number;
  stdOosScore?: number;
}

export interface BacktestJob {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  type: string;
  assetId?: string;     // NEW (Arc 1b) — server now always sets; optional for back-compat with cached responses
  parameters: BacktestParams;
  progress: BacktestProgress;
  result?: BacktestResultData;
  walkForwardResult?: WalkForwardResultData;
  createdAt: string;
  completedAt?: string;
  error?: string;
  optimizeResult?: GridSearchResult;
  gridSpace?: Record<string, any>;
  fitnessPreset?: string;
  topN?: number;
  trainDays?: number;
  testDays?: number;
  stepDays?: number;
  cpcvResult?: CpcvResult | null;
  cpcvN?: number;
  cpcvK?: number;
  cpcvEmbargoDays?: number;
  tpeResult?: TpeResultData | null;
  tpeTrialBudget?: number;
  sweepOverlaySc?: boolean;
  overlaySweepBudget?: string;
}

export interface ConfigEntry {
  params: BacktestParams;
  source_job_id?: string | null;
  /** When true, the DELETE endpoint refuses to remove this preset and the
   *  panel dropdowns hide it — for test-fixture presets like gold_mt_y10_v1
   *  that live in the store only so backend tests can Assert ContainsKey.
   *  Defaults undefined (== false) for legacy entries; see
   *  BacktestConfigService.ConfigEntry.SystemLocked. */
  system_locked?: boolean;
}

export interface BacktestConfigStore {
  configs: Record<string, ConfigEntry>;
  active: string;
  environment?: string;
}

export interface ScheduleEntry {
  id: string;
  cron: string;
  type: string;
  assetId?: string;     // NEW (Arc 1b)
  parameters: BacktestParams;
  lastRun?: string;
  nextRun?: string;
}


export interface HolidayInfoData {
  name: string;
  date: string;
}

export interface CalendarStatus {
  tradingDay: boolean;
  ukHoliday: HolidayInfoData | null;
  nyseHoliday: HolidayInfoData | null;
  weekend: boolean;
  nextTradingDay: string | null;
  reason: string | null;
  exogenousEvents?: ExogenousEventData[];
  noHold?: boolean;
  imrMode?: string;
  pastTradingDays?: string[];
  fundClosures?: Record<string, HolidayInfoData>;
  nextDealingDay?: Record<string, string>;
}

export interface FundHolidayRow {
  date: string;
  name: string;
  ukHoliday: boolean;
  nyseHoliday: boolean;
  fundClosure: boolean;
  fundClosureName: string | null;
}

export interface ExogenousEventData {
  type: string;
  label: string;
  active: boolean;
  description: string;
}

export interface DealResultData {
  accountId: number;
  accountName: string;
  direction: string;
  amount: number;
  dealStatus: string;
  reference?: string;
}

export interface ExecutionStatus {
  status: 'idle' | 'waiting' | 'executed' | 'hold' | 'missed' | 'error' | 'no_trade';
  decision: string;
  signalValue?: number;
  thresholdValue?: number;
  executedAt?: string;
  error?: string;
  deals: DealResultData[];
  monitoringInstrument?: string;
  monitoringDirection?: string;
  monitoringThreshold?: number;
  monitoringActive?: boolean;
  vixOpen?: number;
  vixCurrent?: number;
  hedgePositionInstrument?: string;
}

export interface AccountSummary {
  cashGbp: number;
  accruedCosts: number;
  netPortfolioValue: number;
}

export interface AssetPositionRow {
  assetType: string;
  description: string;
  units: number;
  avgCost: number;
  markPrice: number;
  markValue: number;
  unrealisedPnl: number;
}

export interface AttributionSummary {
  alphaZScore: number | null;
  fvDeltaBps: number | null;
  combinedSignal: number | null;
}

export interface ProvisionalDealEntry {
  fund: string;
  assetType: string;
  side: 'BUY' | 'SELL';
  phase: 'PreStrike' | 'PostStrikeEstimated' | 'Booked';
  units: number;
  priceGbp: number | null;
  priceSource: 'none' | 'strike_snapshot' | 'live_synth' | 'contract_note';
  amountGbp: number | null;
  strikeUtc: string;
  unitsEstimated: boolean;
  /** plan | hl | hl_manual — hl_manual = an HL order with no executed leg (a deal placed in the HL app). */
  origin?: 'plan' | 'hl' | 'hl_manual';
}

export interface BalanceSheetData {
  date: string;
  account: AccountSummary;
  positions: AssetPositionRow[];
  attribution: AttributionSummary;
  /** Spec 2026-10-06: today's executed-but-not-reconciled deals, all funds. */
  provisional?: ProvisionalDealEntry[];
}

export interface DailyPnlData {
  date: string;
  bgaMtmPnl: number;
  realisedPnl: number;
  costs: number;
  netPnl: number;
  bgaSynthPnl: number;
  fvEdgePnl: number;
  alphaSpreadPnl: number;
  fxPnl: number;
  navEstimationVariance: number;
  cumulativePnl: number;
  cumulativeRealisedPnl: number;
  // Convention B (closed-trade FIFO original-cost basis) — paolo companion
  // 2026-08-23. Absent from older builds; treat as optional.
  cumulativeClosedTradePnl?: number;
  hedgePnl: number;
}

export interface NavDeltaData {
  date: string;
  syntheticNav: number | null;
  inferredBgNav10: number | null;
  // JH 12:00-UK strike anchor (V061, parity Phase 2). Null on BGA rows.
  // NOTE: JH NAV values are in PENCE (BGA rows store pounds).
  inferredNav12?: number | null;
  officialNav: number | null;
  delta1Bps: number | null;
  delta2Bps: number | null;
  delta3Bps: number | null;
  synthVsOfficialBps: number | null;
  executionGapBps: number | null;
  actionTaken: string | null;
  // V060 (2026-07-18): FVA predicted-vs-realized residual tracking.
  // predicted = frozen 07:40Z fva_correction (bps); residual = predicted −
  // realized official return (bps) — the λ-model error series. Null before
  // 2026-07-18 and on days without a decision snapshot.
  fvaPredictedBps?: number | null;
  fvaResidualBps?: number | null;
  // V080 (2026-08-18): true when fvaPredictedBps/fvaResidualBps were booked
  // in shadow (monitoring-only) mode — the engine's gap-correction was
  // never applied to the live decision. See NavDelta.FvaShadow.
  fvaShadow?: boolean;
}

/** GET /api/swing-watch (spec 2026-10-17 §6). snake_case, matching the C# [JsonPropertyName] records. */
export interface SwingRow { date: string; gap_bp: number; z: number | null; unusual: boolean; deal: 'BUY' | 'SELL' | 'MIXED' | null; next_gap_bp: number | null; given_back_pct: number | null }
export interface SwingWatchResult { window_days: number; sd_bp: number | null; band_bp: number | null; rows: SwingRow[]; buy_mean_bp: number | null; buy_count: number; sell_mean_bp: number | null; sell_count: number; other_mean_bp: number | null; other_count: number; unusual_count: number; unusual_on_deals: number; verdict_deals: number; verdict: string; verdict_t: number | null }
export interface SwingFundView { fund: Fund; display_name: string; swing_priced_since: string | null; result: SwingWatchResult | null; error: string | null }
export interface SwingWatchResponse { funds: SwingFundView[]; not_swing_priced: { fund: Fund; display_name: string; note: string | null }[] }

/**
 * One row of `synthetic_bga_kt`. K_t is the de Prado ETF Trick synthetic price
 * for the BGA basket. `date` is the calc-date (US-close-of-day t); `navAtTPlus1`
 * is the published BGA NAV on the following business day (BG strikes at 10:00 UK
 * on t+1 using US-close(t) prices — see `project_bga_nav_dating_convention.md`).
 * `errBps = (k - navAtTPlus1) / navAtTPlus1 × 10_000`.
 */
export interface EtfTrickKtData {
  date: string;
  k: number;
  returnBps: number;
  coveredWeightPct: number;
  contributingTickers: number;
  navAtTPlus1: number | null;
  errBps: number | null;
  dragBpsPerDay: number;
  source: string;
  /**
   * Production aggregate (two-point endpoint) compounded NAV in pence, as
   * computed from the raw basket (not BG's fair-valued strike). Visualises
   * the structural FV drift BG corrects nightly. Null when the variants CSV
   * doesn't cover this date.
   */
  aggPrice: number | null;
  /**
   * FV-corrected variant: aggregate − 0.67·GBPUSD_return compounded with the
   * same seed as `aggPrice`. The 0.67 comes from the joint regression in
   * `project_aggregate_fv_residual_2026-05-10`.
   */
  fvPrice: number | null;
}

/**
 * One minute of intraday K_t (de Prado ETF Trick on the BGA basket at
 * 1-minute resolution). Source: `basket_kt_intraday` (V041) populated by
 * `EtfTrickIntradayService`. `tsUtc` is ISO-8601 UTC with `Z`.
 */
/**
 * Composed "K_t now in NAV pence" — the live present-moment K_t value in
 * NAV pence terms. Returned from /synthetic/bga/kt-now-pence. When inputs
 * are missing, only `available: false` + the *Available fields are set;
 * everything else is null and the consumer should render a degraded state.
 */
export interface KtNowPenceData {
  available: boolean;
  // Populated when available = true
  kNowPence: number | null;            // ← the headline number, in pence
  navAnchorPence: number | null;       // struck NAV used as anchor
  navAnchorDate: string | null;        // ISO date label of the anchor NAV
  returnSinceStrikeBps: number | null; // basket return since the strike, bps
  kNow: number | null;                 // raw intraday K_t (100-base)
  kAnchor: number | null;              // raw intraday K_t at the strike
  kNowTsUtc: string | null;            // timestamp of K_now row
  kAnchorTsUtc: string | null;         // timestamp of K_anchor row
  strikeUtc: string | null;            // computed strike timestamp
  // Populated when available = false (per-input availability flags)
  reason?: string;
  kNowAvailable?: boolean;
  kAnchorAvailable?: boolean;
  navAvailable?: boolean;
}

export interface BasketKtIntradayData {
  tsUtc: string;
  kValue: number;
  basketReturn: number;
  coveredWeightPct: number;
  contributingTickers: number;
  holdingsSnapshot: string;
  fxRate: number | null;
  source: string;
}

export interface MlPredictionData {
  available?: boolean;
  date: string;
  model: string;
  modelPath: string;
  pBuy: number;
  pHold: number;
  pSell: number;
  predicted: number;
  decision: string;
  allocation: number;
  buyThreshold: number;
  features: Record<string, number>;
  hedgeActive: boolean;
  timestamp: string;
}

export interface UserProfile {
  userId: string;
  email: string;
  // V118 (linked users): 'viewer' is a read-only account linked under a
  // guardian's contact email (e.g. Paolo's daughters) — Task 4 enforces
  // read-only server-side, Task 9 hides write controls in the UI.
  role: 'superuser' | 'standard' | 'viewer';
  hlUsername: string | null;
  hlAccountId: number | null;
  // Multi-strategy allocation (V065, spec 2026-07-26-multi-strategy-
  // allocation-design.md §6): the caller's BGA/JH/GOLD enable+weight set.
  // Present on /api/auth/me; absent on /api/auth/login.
  allocations?: FundAllocation[];
  // Japan Phase 2 (per-user fund entitlement, spec 2026-09-27, Task 3):
  // the funds this user may see/use, e.g. ["BGA","JH","GOLD","GGE","OTP"];
  // "JAPAN" is appended when granted. Present on /api/auth/me.
  entitledFunds?: string[];
  // Task 5 fix round 1 (R10, superuser view-as, 2026-09-28): present ONLY
  // while a superuser is viewing as another user. `role` above stays the
  // ACTING superuser's own role ("superuser") — every other field
  // (userId, email, hlUsername, allocations, entitledFunds)
  // is the TARGET's. `viewingAs.role` is the target's actual role.
  viewingAs?: {
    userId: string;
    label: string;
    role: 'superuser' | 'standard' | 'viewer';
    // Final fix wave Minor 7: true only when the acting superuser may
    // connect this target's HL (R13 — his own linked users).
    canConnectHl?: boolean;
  };
  // 2026-09-28 (superuser view-only access to gated funds): the funds this
  // user may VIEW read-only — entitledFunds plus, for a superuser, every
  // entitlement-gated fund (JAPAN) as a preview. Never implies dealing.
  // Present on /api/auth/me only.
  viewableFunds?: string[];
  // Spec 2026-09-30 per-user fund access: live funds the admin has NOT restricted for this user
  // (BGA/JH/GOLD/GGE order), plus "JAPAN" when entitled. Absent on /login and on an API that
  // predates the feature — consumers must then fail OPEN for live funds (see useFundEnablement).
  accessibleFunds?: string[];
}

// HL SMS verification step (login-step-three, 2026-09-21): phase 1 of the
// sign-in parked at HL's code prompt. maskedPhone is HL's rendering of the
// recipient ("07******078") or null when it could not be parsed.
export interface LoginVerificationPending {
  verificationRequired: true;
  maskedPhone: string | null;
}

export type LoginResponse = UserProfile | LoginVerificationPending;

export interface HlStatus {
  hlSessionActive: boolean;
  // A login is parked at HL's code prompt — the code HL texted can be entered directly.
  verificationPending: boolean;
  // Background HL access is latched off until the operator signs in.
  verificationRequired: boolean;
  maskedPhone: string | null;
  // HL has temporarily disabled the username (too many login attempts, 2026-09-22).
  accountLocked?: boolean;
  lockMessage?: string | null;
}

export function isLoginVerificationPending(r: LoginResponse): r is LoginVerificationPending {
  return (r as LoginVerificationPending).verificationRequired === true;
}

// Multi-strategy allocation (spec §6): wire shape shared by
// GET/PUT /api/user/fund-allocations and the /me `allocations` array.
export interface FundAllocation {
  fund: Fund;
  enabled: boolean;
  weightPct: number;
  // Shared-pot users only (spec 2026-10-02 §6): tier 1 = filled first; reservePct = % of pot value
  // the fund's tier leaves unspent. Absent on every legacy payload.
  tier?: number | null;
  reservePct?: number;
}

// Spec 2026-10-02 §6.1: a shared-pot user's GET/PUT /api/user/fund-allocations body. A legacy user
// gets (and sends) the bare array — tell them apart with Array.isArray (see
// components/strategy/allocationModel.ts).
export interface SharedPotFundAllocations {
  allocationModel: 'shared_pot';
  allocations: FundAllocation[];
}
export type FundAllocationsResponse = FundAllocation[] | SharedPotFundAllocations;

// AllocationHero (2026-09-05): wire shape of GET /api/user/allocation-status.
// residual.available=false = Redis key absent/expired/malformed — distinct
// from a real £0 measurement (fully deployed).
export interface AllocationStatusFund {
  fund: PotFund;
  enabled: boolean;
  weightPct: number;
  positionGbp: number;
}

export interface AllocationResidual {
  available: boolean;
  totalGbp: number;
  date: string | null;
}

export interface AllocationStatus {
  funds: AllocationStatusFund[];
  cashGbp: number;
  capitalGbp: number;
  residual: AllocationResidual;
  // Spec 2026-10-02 §6.4: present ('shared_pot') only for a shared-pot user — weights are then
  // per-fund entry targets that may sum above 100.
  allocationModel?: 'shared_pot';
}

export interface GoldRampStatus {
  promoted: boolean;
  shadowDays: number;
  holdingsSnapshotAgeDays: number | null;
  runbook: string;
}

export interface FundSellResult {
  sedol: string;
  accountId: number;
  status: string;
  reference: string | null;
}

export interface LiquidateFundResponse {
  fund: 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'OTP';
  results: FundSellResult[];
}

// Target-weight rebalancing plan Task 5 (spec 2026-08-05-target-weight-
// rebalancing.md §3.4/§5): wire shape for GET /api/user/dealing-plan,
// backing DealingPlanCard. `basis` is DealingPlanBasisDto re-emitted
// camelCase server-side from the leg's BasisJson.
export interface DealingPlanBasis {
  targetPct: number;
  actualPct: number;
  gapPct: number;
  signal: string;
  totalCapitalGbp: number;
}

export interface DealingPlanLeg {
  fundId: string;
  side: string;
  accountId: number;
  // Human label from user_account_allocations ("SIPP"); "acct {id}" fallback.
  accountName: string;
  sedol: string;
  estUnits: number | null;
  estAmountGbp: number;
  basis: DealingPlanBasis;
  status: string;
  statusReason: string | null;
  executedTradeRef: string | null;
}

export interface DealingPlanView {
  planDate: string;
  status: string;
  // The caller's current Auto-Execute switch; absent on an older API (treated as true).
  autoExecute?: boolean;
  legs: DealingPlanLeg[];
}

export interface StrategyRecord {
  strategyId: string;
  name: string;
  configJson: string;
  createdBy: string;
  sharpeRatio: number | null;
  isPublished: boolean;
  createdAt: string;
}

export interface AdminUserRecord {
  userId: string;
  email: string;
  hlUsername: string | null;
  hlAccountId: number | null;
  isSuperUser: boolean;
  isUserOfLastResort: boolean;
  isBillable: boolean;
  isActive: boolean;
  activeStrategyId: string | null;
  credentialsStatus: string;
  credentialsInvalidSince: string | null;
  allocatedAmountGbp: number | null;
  createdAt: string;
  updatedAt: string;
  // Per-user WhatsApp routing (2026-09-28): the server never returns the
  // full number — only whether one is set and a masked form (last 3 digits).
  hasWhatsapp?: boolean;
  whatsappNumberMasked?: string | null;
  // V118 linked users: set on a linked viewer (e.g. Paolo's daughters). A
  // linked account never has a WhatsApp of her own — her alerts always go
  // to this guardian's number (2026-09-30).
  guardianUserId?: string | null;
  displayName?: string | null;
}

// Spec 2026-09-30 per-user fund access: the admin grid (GET /api/admin/fund-access).
// PR 0 (spec 2026-10-05): derived from the Fund registry, not a hand-written literal —
// every fund but OTP (which has no entitlement/access-grid concept).
export type FundAccessFund = Exclude<Fund, 'OTP'>
export interface FundAccessCell {
  allowed: boolean
  heldUnits: number
}
export interface FundAccessRow {
  userId: string
  label: string
  role: 'superuser' | 'standard' | 'viewer'
  funds: Record<FundAccessFund, FundAccessCell>
}

// Task 6 (spec 2026-09-28 linked users): a read-only viewer created under
// the calling superuser's guardianship (LinkedUserAdminEndpoints.cs).
export interface LinkedUserRecord {
  userId: string;
  displayName: string;
  hlUsername: string;
}

// Task 6: the switcher list — never carries WhatsApp numbers or
// credentials, only what's needed to pick who to view as.
export interface SwitchableUser {
  userId: string;
  label: string;
  role: 'superuser' | 'viewer' | 'standard';
  isLinked: boolean;
  // Fix (linked users, HL user ID correction): present (non-null) only for
  // a linked row (isLinked true) — the current value the "Edit HL user ID"
  // action changes.
  hlUsername?: string | null;
  // True when this linked row's guardian is the calling superuser — the
  // Admin panel only offers "Edit HL user ID" when this is true, mirroring
  // the endpoint's own guardian check.
  isMine?: boolean;
}

// Fund shortlist (PR 2b) — /api/admin/strategy/fund-shortlist/* (FundShortlistEndpoints.cs).
// jsonb blocks are PascalCase and may carry "NaN"/"Infinity" strings — read them through num().
export type JsonNum = number | string | null | undefined;
export interface ShortlistFund {
  fundKey: string; fundName: string; sedol: string | null; isin: string | null; shareClass: string | null;
  sector: string; future: string; status: 'listed' | 'proposed' | 'live' | 'evicted' | 'rejected' | string;
  statusReason: string | null; pricingCeiling: number | null; pricingCeilingSetAt: string | null;
  asOf: string | null; nDays: number | null; betaSame: number | null; betaNext: number | null;
  passthroughPct: number | null; edgeLeftPct: number | null; expectedEdgePct: number | null;
  edgeBpPerDay: number | null; tStatNext: number | null; corrNext: number | null;
  edgeLeft250dPct: number | null; ruleCagrPct: number | null; bhCagrPct: number | null;
  localCcyEdgeLeftPct: number | null; altFuture: string | null; altExpectedEdgePct: number | null;
  altCorrNext: number | null; unchangedNavShare: number | null; flags: string[]; rank: number | null;
  drops: Record<string, JsonNum> | null; audit: Record<string, unknown> | null;
  pricingSource: Record<string, unknown> | null;
  valuationTimeUk: string | null; ocfPct: number | null; sizeGbpM: number | null;
  levy: string | null; fairValue: unknown; hlUrl: string | null; facts: Record<string, unknown> | null;
  // Edge-source figures in bp/day (Plan C). Absent on rows scored before they existed.
  stale250Bp?: number | null; stale120Bp?: number | null; continuation250Bp?: number | null;
  drift250Bp?: number | null; payoff250Bp?: number | null; notRankableReason?: string | null;
  // Signal-level diversification and the pot score over 250 decision days (spec 2026-10-30 §8a). Sharpe and
  // correlations are plain numbers; closestPotFund is a shortlist fund key.
  edgeSharpe250?: number | null; signalCorrPot250?: number | null; closestPotFund?: string | null;
  closestPotCorr?: number | null; potScore250?: number | null;
}
export interface ShortlistSector { sector: string; cap: number; listedCount: number; funds: ShortlistFund[] }
export interface ShortlistGate { pass: boolean; failures: string[]; japanFailures?: string[]; japanCorrInBand?: boolean; japanFullCorr?: number | null }
/** Spec §8b: the fixed yardstick the pot score is measured against, by name and fund count only (never members). */
export interface ShortlistYardstick { name: string; fundCount: number }
export interface FundShortlistResponse {
  asOf: string | null; yardstick?: ShortlistYardstick | null; gate: ShortlistGate; sectors: ShortlistSector[];
  live: ShortlistFund[]; evicted: ShortlistFund[]; rejected: ShortlistFund[]; proposed: ShortlistFund[];
}
export interface ShortlistChangeRow { changeId: number; at: string; fundKey: string; action: string; reason: string | null; detail: unknown; runId: string | null }
export interface ShortlistCollectorRun { runId: string; startedAt: string; finishedAt: string | null; status: string; fundsOk: number; fundsFailed: number; detail: unknown }
export interface ProspectorRunRow { runId: string; startedAt: string; finishedAt: string | null; status: string; model: string; budgetUsd: number; spendUsd: number; searches: number; proposed: number; rejected: number; duplicates: number; detail: unknown }
export interface LevyCheckSummaryRow { fundId: string; checks: number; suspected: number; mismatch: number; lastTradeDate: string | null; lastOutcome: string | null; maxAdverseBp: number | null }

// Task 8 (user-level strategy assignment): types for the /api/admin/strategy/*
// surface (StrategyAdminEndpoints.cs). `params`/`default_params` are nested
// JSON objects on the wire (server-side JsonElement), not stringified JSON.
export interface StrategyCatalogEntry {
  strategy_id: string;
  display_name: string;
  description: string;
  provider_class: string;
  default_params: Record<string, unknown>;
  is_active: boolean;
  admin_only: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserStrategyAssignment {
  user_id: string;
  strategy_id: string;
  params: Record<string, unknown>;
  assigned_by: string;
  assigned_at: string;
  reason: string | null;
}

export interface UserStrategyRow extends UserStrategyAssignment {
  email: string;
  // 2026-10-05: every active user is listed; linked users share their guardian's email, so rows carry display_name.
  display_name?: string | null;
  is_viewer?: boolean;
  // false ⇒ the user has no assignment row and runs the default "static".
  has_assignment?: boolean;
}

export interface UserStrategyHistoryEntry {
  strategy_id: string;
  params: Record<string, unknown>;
  effective_from: string;
  effective_to: string | null;
  assigned_by: string;
  reason: string | null;
}

export interface StrategyEventRow {
  event_id: string;
  user_id: string | null;
  type: string;
  timestamp: string;
  message: string;
  detail: string | null;
  fund_id: string | null;
}

export interface DailyRiskMetricRow {
  date: string
  stream: string
  sharpe20d: number | null
  sharpe60d: number | null
  sharpe120d: number | null
  sortino20d: number | null
  sortino60d: number | null
  sortino120d: number | null
  maxDd20d: number | null
  maxDd60d: number | null
  maxDd120d: number | null
  winRate60d: number | null
  winRate120d: number | null
  bgaCycleWinRate60d: number | null
  bgaCycleWinRate120d: number | null
  bgaCycleCount60d: number | null
  bgaCycleCount120d: number | null
  // 20d tier (V146, 2026-10-27) — optional: rows written before V146 carry no 20d value.
  bgaCycleWinRate20d?: number | null
  bgaCycleCount20d?: number | null
  otpCycleWinRate20d?: number | null
  otpCycleCount20d?: number | null
  // OTP trade-cycle win rate + count. Server populates these for `otp` and
  // `combined` streams; null for `bga`. Symmetric to bgaCycle*. The TS
  // type was missing these fields, so RiskPanel's `combined`-row "Win Rate"
  // column couldn't compute a true aggregate (it fell back to showing JUST
  // BGA's cycle rate, which made the value identical to the bga row).
  // Added 2026-05-28 to fix the desktop/mobile combined-win-rate bug.
  otpCycleWinRate60d: number | null
  otpCycleWinRate120d: number | null
  otpCycleCount60d: number | null
  otpCycleCount120d: number | null
  profitFactor60d: number | null
  profitFactor120d: number | null
  cagr60d: number | null
  cagr120d: number | null
  currentDdPct: number | null
  sharpeDev120d: number | null
  sortinoDev120d: number | null
  ddDev: number | null
  classification: string
  // 2026-08-05 risk-screen-per-fund Task 7: per-fund warm-up state. Server
  // (DailyRiskMetricRow.IsWarmingUp/WarmupNonzeroDays, C#) serialises with
  // the same camelCase policy as every other field on this row — NOT
  // snake_case, despite what an earlier draft of this task assumed.
  isWarmingUp: boolean
  warmupNonzeroDays: number | null
}

export interface RiskBaseline {
  stream: string
  metric: string
  value: number
  source: string | null
}

export interface RiskThreshold {
  metric: string
  greenMaxDev: number
  amberMaxDev: number
  // V100 per-fund scoping: 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'COMBINED'
  fund: string
}

export interface MetricSummary {
  mean: number
  median: number
  stdDev: number
  min: number
  max: number
  p5: number
  p95: number
}

export interface CpcvDistribution {
  sharpe: MetricSummary
  sortino: MetricSummary
  cagr: MetricSummary
  maxDrawdown: MetricSummary
  profitFactor: MetricSummary
  winRate: MetricSummary
  fitness?: MetricSummary
}

export interface CpcvPath {
  pathId: number
  sharpeRatio: number
  sortinoRatio: number
  cagrPct: number
  maxDrawdownPct: number
  profitFactor: number
  winRate: number
  tradeCount: number
  totalPnl: number
  maxConsecutiveLosses?: number
  fitness?: number | null
  equityCurve: number[]
}

export interface CpcvResult {
  n: number
  k: number
  embargoDays: number
  numCombinations: number
  numPaths: number
  elapsedMs: number
  paths: CpcvPath[]
  distribution: CpcvDistribution
}

export interface TpeResultGroup {
  groupKey: string
  label: string
  trialCount: number
  bestFitness: number
  bestParams: BacktestParams
  sharpe: number
  cagr: number
  maxDrawdownPct: number
  winRate: number
  tradeCount: number
  deflatedSharpe: number
  fullResult?: BacktestResultData
}

export interface TpeResultData {
  totalTrials: number
  elapsedMs: number
  fitnessPreset: string
  groups: TpeResultGroup[]
  overallBest?: TpeResultGroup
}

export interface OhlcData {
  open: number
  high: number
  low: number
  close: number
}

export interface CandleData {
  timestampUtc: string
  bga: OhlcData
  nas100: OhlcData
  vix: OhlcData
  gbpusd: OhlcData
  signalDecision: string
  signalValue: number
  hedgeThreshold: number
  overlayThreshold: number
  shortConvThreshold: number
  longConvThreshold: number
  // Momentum-gate inputs persisted from V025 (2026-04-24). Null on rows
  // flushed before the migration, and legitimately null when imrMode
  // wasn't momentum. The BGA trend line colours by these when present.
  imrWz: number | null
  wzThreshold: number | null
}

export interface SignalTransition {
  timestampUtc: string
  from: string
  to: string
}

export interface CurrentThresholds {
  hedge: number
  overlay: number
  shortConv: number
  longConv: number
}

export interface HourlySnapshotResponse {
  candles: CandleData[]
  signalTransitions: SignalTransition[]
  currentThresholds: CurrentThresholds
  gbpusd10amRate: number
}

export interface SentimentHistoryPoint {
  date: string             // YYYY-MM-DD UTC
  sentiment: number | null
  coveragePct: number | null
  nArticles: number
  bgaClose: number | null
  /** 20-day rolling causal z-score over coverage-non-zero days. null until
   * the warm-up completes. Reframes "is sentiment low" as "unusually low
   * for the recent regime" — robust to AV's positive base-rate bias. */
  sentimentZ20d: number | null
}

export interface SentimentHistoryResponse {
  points: SentimentHistoryPoint[]
}

export interface SentimentTickerRow {
  ticker: string
  sentiment: number | null         // relevance-weighted ticker sentiment
  nArticles: number
  maxRelevance: number             // 0..1
  weightPct: number                // basket weight as % (e.g. 6.94 for 6.94%)
  contribution: number             // sentiment × weight (decimal)
  topArticleUrl: string | null
}

export interface SentimentTickerSummaryResponse {
  date: string
  tickers: SentimentTickerRow[]
}

// In-progress hour OHLC snapshot from the server's HourlySnapshotService
// in-memory tracker. Used to render an accurate forming bar that survives
// page refresh (the client used to accumulate OHLC locally and lose state
// on reload). null fields = that series hasn't been sampled yet this hour.
export interface FormingCandleResponse {
  hourUtc: string
  bga: OhlcData | null
  nas100: OhlcData | null
  vix: OhlcData | null
  gbpusd: OhlcData | null
  signalDecision: string | null
  signalValue: number
  imrWz: number | null
  wzThreshold: number | null
}

export interface NewsTickerItem {
  ticker: string
  headline: string
  source: string | null
  url: string
  timePublished: string
  relevance: number
  sentimentScore: number | null
  sentimentLabel: string | null
}

export interface GoldNavSummary {
  available: boolean;
  reason?: string;
  lastNavDate?: string;
  lastNavValuePence?: number;
  cagrFull?: number | null;
  volFull?: number | null;
  maxDdFull?: number | null;
  cagrRecent5y?: number | null;
  sharpeRecent5y?: number | null;
}

export interface GoldHolding {
  ticker: string | null;
  name: string | null;
  weightPct: number;
  isin: string | null;
  sedol: string | null;
  currency: string | null;
  venue: string | null;
}

export interface GoldHoldingsResponse {
  available: boolean;
  reason?: string;
  asOfDate?: string;
  source?: string;
  totalWeightPct?: number;
  holdings?: GoldHolding[];
  sourceFile?: string;
}

export interface GoldVolRegimePoint { d: string; c: number }
export interface GoldVolRegimeResponse {
  available: boolean;
  reason?: string;
  latestDate?: string;
  latestClose?: number;
  mean30?: number;
  pctile252?: number;
  regime?: 'low' | 'normal' | 'elevated' | 'extreme';
  sparkline?: GoldVolRegimePoint[];   // last 90 closes — mini bar
  history?: GoldVolRegimePoint[];     // last 252 closes — full chart
}

// /api/gold/xauusd — XAUUSD spot (USD/oz) for the gold dashboard's XAU mini
// spark + the full /charts/gold-xau TV chart. Same {d,c} point shape as GVZ.
export interface GoldXauResponse {
  available: boolean;
  reason?: string;
  latestDate?: string;
  latestClose?: number;
  ret1dPct?: number | null;
  hi252?: number;
  lo252?: number;
  sparkline?: GoldVolRegimePoint[];   // last 90 closes — mini bar
  history?: GoldVolRegimePoint[];     // last 252 closes — full chart
}

// /api/gold/treasury-yields — US Treasury yields (percent). 10y is the
// primary series (gold headwind); 5y/30y come along as context lines.
export interface GoldTreasuryYieldPoint {
  d: string;
  y10: number;
  y5: number | null;
  y30: number | null;
}
export interface GoldTreasuryYieldsResponse {
  available: boolean;
  reason?: string;
  latestDate?: string;
  latest10y?: number;
  latest5y?: number | null;
  latest30y?: number | null;
  chg1dBps10y?: number | null;
  sparkline?: GoldVolRegimePoint[];        // last 90 10y closes — mini bar
  history?: GoldTreasuryYieldPoint[];      // last 252 rows — full chart
}

export interface GoldSynthNavPoint {
  d: string;
  k: number | null;
  n: number | null;
  // Gold ex-dividend day: the Income NAV drops by the distribution the synth
  // never sees — excluded from error stats, marked on the chart.
  xd?: boolean;
}
// GET /api/gold/kt-latest — thin passthrough of synth_nav:gold:kt_latest
// (07:35 UK publisher). kGbp is the K_t synthetic NAV level in PENCE
// (field name predates the pence audit; official gold NAV ~111p).
//
// strike-anchor multifund Task 15 (2026-08-20): the publisher's payload now
// also carries strikeState/strikeAtUtc/anchorCoverage — the 3-branch
// pre/post/degraded state machine consumed by Task 21's useStrikeAnchor
// composable. strikeState defaults to undefined on older cached payloads
// (pre-Task-15 backend) — the composable treats that as 'pre'.
export interface GoldKtLatestAnchorCoverage {
  covered: number;
  total: number;
}
// Per-ticker K_t residual attribution response (2026-08-23 Phase-1
// enhancement #3). GET /api/synthetic/{fund}/kt-attribution decomposes
// the daily K_t vs official-NAV residual into per-holding contributions
// Δᵢ = wᵢ × (r_ticker_i − r_nav). BGA + JH only; gold/gge fall into the
// `{ available: false, reason }` shape (distinct daily pipeline path).
export interface KtAttributionRow {
  ticker: string;
  weight: number;                     // 0.0-1.0
  tickerReturnBps: number;            // per-ticker day-over-day return in bps
  contributionBps: number;            // wᵢ × r_ticker_i × 10⁴ (contribution to K_t return)
  residualContributionBps: number;    // wᵢ × (r_ticker_i − r_nav) × 10⁴ (share of residual)
}
export interface KtAttributionResponse {
  available: boolean;
  fund?: string;
  date?: string;
  prevDate?: string;
  navReturnBps?: number;
  ktReturnBps?: number;
  residualBps?: number;
  contributingTickers?: number;
  topContributors?: KtAttributionRow[];
  reason?: string;
}

// Task 11 (2026-09-16 gold/GGE strike-anchor arc PR D). Per-fund shadow-mode
// strike-anchor freshness from GET /api/system/strike-anchor-health —
// StrikeAnchorHealthEndpoint.cs. Drives StrikeAnchorHealthTile.vue's
// persistent 4-row status display. status is server-derived (green/amber/
// red/grey — see the endpoint's own doc comment for the full derivation
// rules); the frontend renders it, it does not re-derive it.
export interface StrikeAnchorFundHealth {
  // 'japan' (2026-10-06): gated — only sent to a caller who can view L&G Japan.
  // 'charteris' (2026-10-11): gated likewise — the noon strike anchor vs the booked strike (CharterisShadowReconciler).
  fundId: 'bga' | 'gold' | 'gge' | 'jh' | 'japan' | 'charteris';
  strikeTimeExpectedUtc: string;
  anchorFreshUtc: string | null;
  verdictPassed: boolean | null;
  cleanSessionCount: number;
  status: 'green' | 'amber' | 'red' | 'grey';
  detail: string;
  // 2026-09-22 extension so the tile can render an informative label
  // ("DRIFT 24.4bp > 1bp") instead of a bare FAILED.
  aggregateDriftBp?: number | null;
  thresholdBp?: number | null;
  canonicalSource?: string | null;
  shadowSource?: string | null;
  perHoldingWorstTicker?: string | null;
  perHoldingMaxDriftBp?: number | null;
  // Task 7 (2026-09-30 BGA threshold-anchor arc §4; JH added 2026-10-01) — accuracy-gated funds (BGA, JH) only. Replaces the
  // parity-drift figures above with the accuracy-grade cutover gate
  // (official NAV vs each rule's predicted return, from
  // AnchorAccuracyMath.Gate over the persisted strike_anchor_accuracy
  // history). thresholdRmseBp carries the fund's CANDIDATE rule's RMSE (name in
  // accuracyCandidateRule). undefined for every other fund, and for BGA/JH too when the
  // endpoint has no accuracy repo wired.
  accuracyScoredDays?: number | null;
  thresholdRmseBp?: number | null;
  lastPrintRmseBp?: number | null;
  accuracyBoundBp?: number | null;
  accuracyGateMet?: boolean | null;
  accuracyExcludedDays?: number | null;
  // Task 7 (2026-10-01 JH NAV-accuracy arc): the candidate rule name the
  // accuracy fields above were scored against (BGA's permanent
  // "threshold"; JH's configured/validated "ah_close"/"threshold"). Also
  // generalised from BGA-only to any accuracy-gated fund.
  accuracyCandidateRule?: string | null;
  // Strike-anchor go-live arc PR-1 (2026-10-14): the accuracy go-live readout
  // for bga/gold/gge — over the newest goLiveWindow NAV dates scored for both
  // rules, each rule's mean |error| vs the official NAV (gold/GGE: "shadow" vs
  // "incumbent"; BGA: its candidate vs "last_print", both C# rules). Gate =
  // scored >= window AND shadow MAE <= incumbent MAE. Shown alongside the
  // parity streak, never instead of it. undefined for every other fund.
  goLiveScoredSessions?: number | null;
  goLiveShadowMaeBp?: number | null;
  goLiveIncumbentMaeBp?: number | null;
  goLiveShadowRule?: string | null;
  goLiveIncumbentRule?: string | null;
  goLiveGateMet?: boolean | null;
  goLiveWindow?: number | null;
  // BGA/JH only: the books go-live readout - the C# shadow vs the Python incumbent,
  // each scored against the fund's booked (contract-note) strike.
  booksScoredSessions?: number | null;
  booksShadowMaeBp?: number | null;
  booksPythonMaeBp?: number | null;
  booksGateMet?: boolean | null;
  booksWindow?: number | null;
  // Which strike-anchor total the HoldingsPanel TOTAL row currently shows.
  totalSource?: 'shadow' | 'incumbent' | null;
}

export interface StrikeAnchorHealthResponse {
  funds: StrikeAnchorFundHealth[];
}

export interface GoldKtLatestResponse {
  available: boolean;
  date?: string;
  kGbp?: number;
  returnFrac?: number;
  coverage?: number;
  coverageTotal?: number;
  rmseBps?: number | null;
  // FX-degradation observability (2026-08-23 Phase-1 K_t enhancement #4).
  // The publisher tracks which currencies had degraded live FX at compute
  // time (source lag, feed drop, capture-window miss). Payload fields exist
  // for both gold + gge (GgeKtLatestResponse aliases this interface);
  // consumed by `fresh-{fund}-kt-fx-degradation` integrity check.
  fxLiveCcys?: string[];
  fxSourceByCcy?: Record<string, string>;
  fxDegradedCcys?: string[];
  fxOldestTickUtc?: string;
  // Downstream-consumable confidence tier (2026-08-23 downstream wiring
  // ship). Derived server-side from `fxDegradedCcys.length`:
  //   ok       — 0 ccys degraded
  //   degraded — 1-2 ccys degraded (WARN — signal still usable)
  //   critical — 3+ ccys degraded (fundamentally-unreliable FX at compute)
  // Alert-only in v1: publisher emits a `kt_fx_degraded` event when != ok
  // but no consumer auto-gates on this field yet. m_t decision / dealing
  // plan can opt-in in a future PR after risk review — see
  // project_backlog_kt_functional_enhancements_2026-08-23 #4 note.
  ktConfidence?: 'ok' | 'degraded' | 'critical';
  // Strike-anchor fields (SDD arc — Task 15). Post-noon-UK, the publisher
  // computes drift from the frozen basket; the panel reads these to decide
  // pre/post/degraded state + badge rendering via useStrikeAnchor.
  strikeState?: 'pre' | 'post' | 'degraded';
  strikeAtUtc?: string | null;
  anchorCoverage?: GoldKtLatestAnchorCoverage | null;
  // Wall-clock UTC of the publisher's Compute call for THIS payload. Kept
  // from the earlier interim POST-STRIKE badge PR (#166) as a supplementary
  // freshness signal; the strike-anchor state machine above supersedes its
  // display role but this field remains for diagnostic surfaces.
  computedAtUtc?: string;
  // Set true when the publisher restored from the local CSV at startup
  // (Redis key was missing). Historical field from #166; the new
  // strikeState='degraded' branch is the canonical restored-state signal.
  restoredFromCsv?: boolean;
  // Gold K_t live overlay bridge (2026-08-31/09-01, PR #311 + Task 5 5-min
  // refresher in GoldKtSynthNavPublisherService). Present only during
  // overlay-eligible windows (pre-07:35 UK, weekends/holidays, restored
  // payloads) — absent otherwise. beta·Δgold from GC futures since the
  // 16:00 NY anchor, layered on top of the frozen base K_t. The P&L bridge
  // already consumes these fields; deriveGoldOverlay (src/utils/goldOverlay.ts)
  // is the web-side single source of truth for the signal-page surfaces.
  // GgeKtLatestResponse aliases this interface — GGE never emits them.
  overlayFrac?: number;
  overlayBeta?: number;
  overlayConfidence?: 'ok' | 'beta_clamped';
  overlayGoldAsOfUtc?: string;
  overlayGoldLastBarUtc?: string;
  overlayComputedAtUtc?: string;
  combinedReturnFrac?: number;
}

// GET /api/gge/kt-latest — GGE parallel of the gold endpoint above, same
// payload shape (GgeKtSynthNavPublisherService.BuildPayloadJson mirrors
// GoldKtSynthNavPublisherService's builder field-for-field). Task 21 flagged
// this route as forward-wired but not yet registered in Program.cs; Task 23
// registered it (mirrors /gold/kt-latest exactly) — the route is live as of
// this commit.
export type GgeKtLatestResponse = GoldKtLatestResponse;

// GET /api/{gold,gge}/strike-total — strike-anchored TOTAL-row return.
// `mode` is always 'incumbent' | 'shadow'; the SPA only promotes the value
// when mode === 'shadow' (see utils/strikeTotal.ts deriveStrikeTotal).
export type StrikeTotalSourceTag = 'pre_strike_daily' | 'since_strike' | 'prior_session_final' | 'anchor_missing'
export interface StrikeTotalResponse {
  available: boolean
  mode: 'incumbent' | 'shadow'
  returnFrac?: number | null
  source?: StrikeTotalSourceTag | string | null
  coveredWeightFrac?: number | null
  liveWeightFrac?: number | null
  strikeTimeUtc?: string | null
  asOfUtc?: string | null
  // GGE (2026-11-03): every priced leg of the shadow K_t's since-strike refresh — the Holdings rows' live prices.
  holdings?: StrikeTotalHolding[] | null
}
// Prices on the leg's own basis: a home-listing leg (anchorSource noon_local_*) is sterling, every other leg its
// listing currency. nowSource: live_tick | beta_proxy | home_live | home_close_adr_chain | home_last | closed_since_strike | …
export interface StrikeTotalHolding {
  ticker: string
  weight: number
  anchorPrice: number
  anchorSource: string
  nowPrice: number
  nowSource: string
}

// GET /api/gold/holdings-latest — thin passthrough of
// synth_nav:gold:holdings_latest (gold-evening-hybrid PR A/B,
// GoldHoldingsHybridService, 60s-cadence US-session publisher). Per-holding
// live/proxy hybrid state for the evening (US-session) gold NAV indication —
// PR C (Task 9) wires the client read side; deriveHoldingsHybrid
// (src/utils/holdingsHybrid.ts) is the single source of truth for Task 10's
// HoldingsPanel. Field shape matches GoldHoldingsHybridService.HoldingPayload
// (Program.cs Services/GoldHoldingsHybridService.cs:487) verbatim.
//
// Plan deviation (Global Constraints "client re-tick" clause, resolved
// 2026-09-04): synth_nav:gold:enriched does NOT expose a live GC price to
// the client — no client re-tick; the 60s server cadence stands.
export interface GoldHoldingRow {
  ticker: string;
  name: string;
  weightFrac: number;
  // 'delayed' (2026-10-15): a Toronto-only name priced from its EODHD
  // (~15 min delayed) quote, rolled forward by beta x dGC since the quote.
  state: 'live' | 'prxy' | 'delayed';
  price: number;
  lastRealPrice: number;
  lastRealAtUtc: string;
  beta: number;
  adjFrac: number;
  currency: string;
  // Strike-freeze snapshot (2026-09-21 paolo P&L defect fix): the price this
  // holding was priced at on the first tick at/after 12:00 UK on
  // `strikeDateAnchored`'s UK date. The P&L pane's `computeGoldTodaysMovePct`
  // uses this when `enrichedNav.sell_pending_at_strike` is true so the
  // "Today"/"Synthetic" figure freezes at the fund's noon valuation point
  // instead of drifting with the US afternoon and post-noon gold-futures
  // moves. Absent (undefined) pre-strike, on a non-dealing day, or when
  // Redis is unwired.
  strikePrice?: number | null;
}
export interface GoldHoldingsLatestResponse {
  available: boolean;
  computedAtUtc?: string;
  strikeDateAnchored?: string; // YYYY-MM-DD
  // The UK date whose 12:00 strike this payload's per-holding strikePrice
  // fields were captured on. Absent pre-strike, on a non-dealing day, or when
  // Redis is unwired. Consumers gate on this matching today's UK date before
  // using strikePrice for the P&L freeze.
  strikeFrozenForDate?: string; // YYYY-MM-DD
  confidence?: string;
  gcNowUtc?: string | null;
  aggregateIndicationFrac?: number;
  coveredWeightFrac?: number;
  liveWeightFrac?: number;
  // Weight on delayed quotes (2026-10-15); absent from older payloads.
  delayedWeightFrac?: number;
  holdings?: GoldHoldingRow[];
}

export interface GoldSynthNavResponse {
  available: boolean;
  reason?: string;
  startDate?: string;
  endDate?: string;
  coverage?: number;
  coverageTotal?: number;
  rmseBps?: number | null;
  series?: GoldSynthNavPoint[];
}

// /api/charts/gold-futures-snapshots — V108 GOLD-vs-GC 15-min candles.
// gold_* is synth_nav:gold:live in GBP pence; gc_* is COMEX GC.n.0 in USD/oz;
// gbpusd_* drives the currency-adjusted chart toggle (convert GC USD→GBP or
// gold pence→USD). No signal/threshold/IMR fields — gold's decision path
// doesn't map to BGA-style triangles, and the gold chart is a single global
// series (no per-user overlays like the BGA/JH quarter-hourly tables).
export interface GoldFuturesOhlc {
  open: number;
  high: number;
  low: number;
  close: number;
}
export interface GoldFuturesSnapshotCandle {
  timestampUtc: string;
  gold: GoldFuturesOhlc;
  gc: GoldFuturesOhlc;
  gbpusd: GoldFuturesOhlc;
}
export interface GoldFuturesSnapshotResponse {
  candles: GoldFuturesSnapshotCandle[];
}

// /api/charts/gge-cl-snapshots (V109) — same shape as gold-futures-snapshots
// above, only the fund + futures pair swap. Powers /charts/ggevscl.
export interface GgeClSnapshotCandle {
  timestampUtc: string;
  gge: GoldFuturesOhlc;    // pence
  cl: GoldFuturesOhlc;     // USD/bbl
  gbpusd: GoldFuturesOhlc; // for the ccy-adjusted toggle
}
export interface GgeClSnapshotResponse {
  candles: GgeClSnapshotCandle[];
}

// 2026-10-10 CHARTERIS-vs-SI candles (V135) — the Charteris clone of BGA-vs-NAS; same shape as gge-cl-candles with
// the pair swapped (charteris = live K_t in pence, si = COMEX SI USD/oz). Gated: 404 unless the caller may view
// CHARTERIS. Powers /charts/charterisvssi.
export interface CharterisSiSnapshotCandle {
  timestampUtc: string;
  charteris: GoldFuturesOhlc; // pence
  si: GoldFuturesOhlc;        // USD/oz
  gbpusd: GoldFuturesOhlc;    // for the ccy-adjusted toggle
}
export interface CharterisSiSnapshotResponse {
  candles: CharterisSiSnapshotCandle[];
}

// /api/gold/synth-nav-live — live gold synthetic NAV polling endpoint
// (Task 4-7 arc: published to Redis every 5s, consumed by useGoldLiveSynthNav
// composable for live dashboard refresh). Snake_case JSON response mirrors
// synth_nav:gold:live Redis key structure; composable transforms to camelCase.
export interface GoldSynthNavLivePerHolding {
  ticker: string;
  weight: number;
  beta: number;
  mktReturnGbp: number;
  // 'proxy' (GC-beta) | 'delayed' / 'stale' (EODHD quote, by age) | 'suspect'
  // (2026-10-15); absent from older payloads.
  state?: string;
}

export interface GoldSynthNavLive {
  anchorPence: number;
  // 'estimate' (2026-10-09): today's NAV as estimated by K_t / the booked noon strike, before it lands.
  anchorKind: 'today' | 'prev' | 'estimate';
  anchorAsOfUk: string;
  liveReturnGbp: number;
  synthNavPence: number;
  tsUtc: string;
  // 'closed' (2026-10-31): Globex shut (weekend, daily halt, CME holiday); the return is kept from the last bar.
  tier: 'live' | 'degraded' | 'critical' | 'closed';
  coveredWeightFrac: number;
  gcDriftPct: number;
  gcLastTickUtc: string;
  perHolding: GoldSynthNavLivePerHolding[];
}

// /api/gge/synth-nav-live — live GGE synthetic NAV polling endpoint
// (mirrors useGoldLiveSynthNav pattern: published to Redis every 5s, consumed
// by useGgeLiveSynthNav composable for live dashboard refresh). Snake_case
// JSON response mirrors synth_nav:gge:live Redis key structure; composable
// transforms to camelCase. CL = Composite Liquidity (GGE's live indicator).
export interface GgeSynthNavLivePerHolding {
  ticker: string;
  weight: number;
  beta: number;
  mktReturnGbp: number;
}

/** Spec 2026-10-04: live synthetic NAV for L&G Japan (GET /api/japan/synth-nav-live?fund=JAPAN). */
export interface JapanSynthNavLive {
  fund: 'JAPAN'
  synthNavPence: number | null
  returnFrac: number | null
  phase: 'live' | 'pre_strike'
  tier: 'live' | 'degraded' | 'critical'
  reason: string | null
  anchorDate: string | null
  anchorNavPence: number | null
  equity: { source: string; price: number | null; tsUtc: string | null; instrumentId: number | null; anchorTokyoClose: number | null }
  fx: { mid: number | null; tsUtc: string | null; anchorFix: number | null }
  computedAtUtc: string
}

export interface GgeSynthNavLive {
  anchorPence: number;
  // 'estimate' (2026-10-09): today's NAV as estimated by K_t / the booked noon strike, before it lands.
  anchorKind: 'today' | 'prev' | 'estimate';
  anchorAsOfUk: string;
  liveReturnGbp: number;
  synthNavPence: number;
  tsUtc: string;
  // 'closed' (2026-10-31): Globex shut (weekend, daily halt, CME holiday); the return is kept from the last bar.
  tier: 'live' | 'degraded' | 'critical' | 'closed';
  coveredWeightFrac: number;
  clDriftPct: number;
  clLastTickUtc: string;
  perHolding: GgeSynthNavLivePerHolding[];
  // 'kt_shadow' (2026-11-03): the move is the shadow K_t's (GGE total_source flag = shadow); absent = CL × β.
  method?: 'kt_shadow';
}

// /api/synthetic/{fund}/kt-tracking — the K_t vs official-NAV scoreboard
// (JH K_t live-parity plan Task 9; gold arm added by gold-productionization
// Task 13). `methods` is keyed by synth method name ("etf_trick",
// "aggregate", ...); stats are daily-return tracking error vs the official
// fund NAV over the requested window.
export interface KtTrackingMethodStats {
  mae_bps: number;
  rmse_bps: number;
  ic: number;
  n: number;
}
export interface KtTrackingResponse {
  fund: string;
  window: 'trailing60' | 'honest';
  from: string | null;
  to: string;
  methods: Record<string, KtTrackingMethodStats>;
  note: string | null;
}

// /api/kt/daily-scoreboard — the dashboard-facing multi-fund sibling of
// /api/synthetic/{fund}/kt-tracking above (K_t tracking dashboard Task 2).
// One row per fund (bga/jh/gge/gold); a fund whose series or NAV can't be
// read renders `available: false` with a `reason` rather than failing the
// whole response.
export interface KtDailyScoreboardFund {
  fund: string;              // "bga" | "jh" | "gge" | "gold"
  seriesFundId: string;      // "bga" | "jh:lag" | "gge" | "gold" (BGA & GGE flipped live; JH stays shadow)
  label: string;             // e.g. "BGA (live)", "JH (shadow :lag)"
  corr: number | null;
  rmseBps: number | null;
  sigmaBps: number | null;   // std of official daily returns over the same aligned window, bps
  ratio: number | null;      // rmseBps / sigmaBps — informational only, never a gate input
  n: number;
  gatePass: boolean;
  available: boolean;
  reason?: string | null;    // present (possibly null) when available=false
  strikeAdjApplied?: boolean; // K_t return includes the 12:00 strike FX + noon fair-value term (tracking-only); optional for older API builds
  strikeAdj?: KtStrikeAdjExplain | null; // worked example for the tile's (i) toggle; null when the term is off or the signal file is missing
}

// Worked example behind the STRIKE-ADJ (i): the fund's live term parameters
// and the latest stored session's prints, all in bps, so the tile can show
// `β × (m_T − m_prev) = 0.30 × (+132.6 − +82.7) = +15.0 bps`.
export interface KtStrikeAdjExplain {
  beta: number;
  triggerBps: number;        // 0 = linear form; >0 = prints at or below the trigger count as 0 (Gold)
  form: 'linear' | 'trigger';
  signalCsv: string;         // e.g. nq_1000_signal.csv
  proxy: string;             // NQ | CL | GC
  clock: string;             // e.g. "10:00 London"
  fxNote: string;
  latestDate: string | null; // latest row carrying an adjustment
  prevDate: string | null;   // its predecessor K_t date
  mtBps: number | null;
  mPrevBps: number | null;
  adjBps: number | null;     // recomputed from the prints
  storedAdjBps: number | null; // the row's strike_adj_bps column
}

export interface KtDailyScoreboard {
  gate: { minCorr: number; maxRmseBps: number };
  windowSessions: number;
  funds: KtDailyScoreboardFund[];
}

// /api/risk/pricing-source — stale-NAV pricing-source detector (spec 2026-09-25).
export interface PricingSourceSample {
  date: string;
  prevDate: string;
  moveBps: number;        // Δm: futures move since the last US close at this strike minus the same at the previous strike
  closeModelBps: number;  // close-based K_t return (prior official US closes)
  navBps: number;         // official NAV return
  residualBps: number;    // navBps − closeModelBps
  dayRatio: number | null; // residual / move (null when |move| < 5 bp)
}

export interface PricingSourceFund {
  fund: Lowercase<PotFund>;
  label: string;           // BGA | JH | GOLD | GGE | JAPAN | CHARTERIS | AMATI | MINERS | JAPANESG | TECH | ELECTRUM | JHTECH | ASIATECH | BARINGSOIL | SPWORLD | SMALLCAP
  proxy: string;           // NQ | GC | CL | NIY | SI | HG
  valuationClock: string;  // e.g. "10:00 London"
  rag: 'green' | 'amber' | 'red';
  reason: string | null;
  passThrough40: number | null;
  passThrough20: number | null;
  edgeLeft40: number | null;
  n40: number;
  latestDate: string | null;
  greenCeiling: number;
  redFloor: number;
  excluded40?: number;       // implausible NAV prints ignored in the 40-day window (bad-print guard)
  betaScale?: number;        // divisor applied to the fitted pass-through: the fund's beta to its reference
                             // future (pack risk.threshold_scale, e.g. TECH 1.27); absent or 1 = unscaled
  sample: PricingSourceSample | null;
}

export interface PricingSourceResponse {
  asOf: string;
  windows: { long: number; short: number };
  funds: PricingSourceFund[];
}

export type SignalHealthStatus = 'green' | 'amber' | 'red';
export interface SignalHealthWindow {
  from: string; to: string; n: number;
  betaY1: number | null; seY1: number | null; pBetaY1Pos: number | null; betaY0: number | null;
  fundBetaRef: number | null; fundR2Ref: number | null; hit: number | null;
  signedBps: number | null; sdXBps: number | null; sdY1Bps: number | null;
}
export interface SignalHealthRollingPoint { to: string; fundBetaRef: number | null; betaY1: number | null; signedBps: number | null }
export interface SignalHealthTier { absZ: string; n: number; hit: number | null; signedBps: number | null }
export interface SignalHealthBucket {
  window: 'last500' | 'prior750'; bucketSd: string; bp: string; n: number;
  betaY0: number | null; betaY1: number | null; hit: number | null; signedBps: number | null;
}
export interface SignalHealthFund {
  fundId: string; label: string; slug: string; ref: string; status: SignalHealthStatus; reason: string; summary: string;
  offsetRows: number | null; acknowledgedUntil: string | null; acknowledgedNote: string | null; staleSince: string | null;
  lastDate: string | null; computedAtUtc: string;
  last250: SignalHealthWindow | null; last125: SignalHealthWindow | null; prior750: SignalHealthWindow | null;
  rolling: SignalHealthRollingPoint[]; tiers: SignalHealthTier[]; buckets: SignalHealthBucket[];
  trigger: { kind: 'absolute' | 'relative'; betaFloor: number | null; bpsFloor: number | null; action: string | null };
}
export interface SignalHealthResponse { asOf: string; funds: SignalHealthFund[] }

// GET /api/risk/rules (spec 2026-11-01 pot de-risking rules). Advisory; dates yyyy-MM-dd.
export interface RiskRuleEpisode { start: string; end: string | null; worstLevel: string; worst: number; trippedOn: string | null; actedOn: string | null }
export interface PotRiskLevel { atPct: number; level: 'amber' | 'red'; action: string }
export interface PotDrawdownStatus {
  state: 'no_data' | 'ok' | 'amber' | 'red'
  drawdownPct: number | null; peakDate: string | null; asOf: string | null; from: string | null; action: string | null
  series: { date: string; value: number }[]
  episodes: RiskRuleEpisode[]
}
export interface FundRuleStatus {
  fundId: string; state: 'too_short' | 'ok' | 'watch' | 'tripped'
  consecutiveBelow: number; sessions: number; asOf: string | null
  cagrPctile20: number | null; sharpePctile20: number | null; action: string | null
  episodes: RiskRuleEpisode[]
}
export interface RiskRulesResponse {
  since: string | null; drawdownLevels: PotRiskLevel[]; fundBelowSessions: number; fundAction: string
  pot: PotDrawdownStatus; funds: FundRuleStatus[]
}

// GET /api/risk/pot-parity (live shared pot vs backtest). camelCase; non-ok statuses carry empty arrays / null metrics.
export type PotParityStatus = 'ok' | 'not_on_pot' | 'not_computed' | 'no_live_trading' | 'not_traded'
export interface PotParityMetrics {
  sessions: number
  liveCagr: number | null; btCagr: number | null
  liveSharpe: number | null; btSharpe: number | null
  correlation: number | null; trackingError: number | null
  cumulativeGapPct: number | null; mismatchDays: number
}
// GET /api/risk/pot-parity/funds: entitled funds, canonical order (per-fund Live vs backtest pills).
export interface PotParityFundEntry { fundId: string; status: 'ok' | 'not_traded' | 'not_computed'; firstLiveDay: string | null }
// Edge-source history (spec 2026-10-30, Plan A #1081) — /api/risk/edge-map and /api/risk/edge-source.
// All figures are bp/day; the UI converts to % per year (× 2.52).
export type EdgeHorizon = '1w' | '1m' | '3m' | '6m' | '1y' | '2y' | '3y'
export interface EdgePoint {
  asOf: string; staleBp: number; continuationBp: number; driftBp: number; unexplainedBp: number
  payoffBp: number; bhBp: number; investedPct: number; n: number
  // V159 (2026-10-31): NAV volatility (bp/day sd over the window) and the rule's CAGR (% a year); null on older rows
  volBp?: number | null; ruleCagrPct?: number | null
}
export interface EdgeMapFund {
  fundId: string; label: string; future?: string | null; status: 'live' | 'candidate'; weightPct: number
  pricingRag: string | null; passThrough40: number | null
  now: EdgePoint; horizons: Partial<Record<EdgeHorizon, EdgePoint | null>>
}
export interface EdgeMapResponse {
  asOf: string; window: number; funds: EdgeMapFund[]
  quadrantWeights: Partial<Record<'staleEdge' | 'staleTrend' | 'noEdge' | 'trendPaid', number>>
}
export interface EdgeSourceResponse {
  fundId: string; window: number; series: EdgePoint[]
  markers: { date: string; kind: string; label: string }[]
}

export interface PotParityResponse {
  status: PotParityStatus
  inception: string | null
  sessions: number
  lastBacktestDay: string | null
  sinceInception: PotParityMetrics | null
  last20: PotParityMetrics | null
  liveSharpePctile: number | null
  liveCagrPctile: number | null
  verdict: string | null
  tooShort: boolean
  lowConfidence: boolean
  series: { date: string; liveIndex: number; btIndex: number | null }[] // btIndex null on live days after the last backtest day
  mismatches: { date: string; fundId: string; liveHeld?: boolean | null }[] // liveHeld: fund view only (null on FOF)
  markers: { date: string; kind: string; label: string; reconstructed: boolean }[]
  liveScopeNote: string | null
  /** Per-fund view only: live days before the fund's first recorded preset are not compared. */
  compareNote?: string | null
}

/**
 * BGA/JH step-aside anchor state — durable prior US-close snapshot from
 * V107's nq_close_snapshot table plus a server-side freshness class.
 * `state` reflects the same threshold the health monitor alarms against,
 * so a red tile means "the WhatsApp alert has fired or is about to fire".
 */
export interface StepAsideAnchorLatest {
  closeTargetUtc: string;
  closeValue: number;
  barTsUtc: string;
  source: string;
  latchedAtUtc: string;
}
export interface StepAsideAnchorState {
  state: 'fresh' | 'aging' | 'stale' | 'missing';
  latest: StepAsideAnchorLatest | null;
  ageHours: number | null;
  thresholdHours: number;
}

// gold-productionization Task 10: /api/signal/gold — the python producer's
// latest:signal:gold payload forwarded verbatim (see src/signal_core/
// gold_mt_signal.py build_payload). Every field is optional here because
// the endpoint returns `{ available: false, fund: 'GOLD' }` when the
// producer has not yet published on a given day (early boot, first run,
// container restart before 08:40 London — post 2026-07-26 clock correction).
export interface GoldSignalParamsEcho {
  step_aside_threshold_bps?: number;
  step_aside_buy_threshold_bps?: number | null;
  step_aside_reentry_bps?: number | null;
  yield_collapse_gate_mode?: string;
  yield_collapse_drop_bps?: number;
  yield_collapse_window_days?: number;
}

// Decision-inputs freshness block (2026-08-07 operator request: surface
// treasury yields + other gold m_t decision inputs on the dashboard, plus
// a morning log confirming the signal is fresh). Mirrors
// gold_mt_signal.build_inputs_summary — built from data the producer
// already loaded for the decision, so it's absent (not populated with
// nulls) on producer builds that predate this feature ("old container"
// degrade path in useIntegrityChecks).
export interface GoldSignalInputsGcBars {
  last_bar_utc: string | null;
  bar_count: number;
  age_min: number | null;
}

export interface GoldSignalInputsTreasury {
  last_row_date: string | null;
  rows_in_window: number;
  window_days: number;
}

export interface GoldSignalInputs {
  gc_bars: GoldSignalInputsGcBars;
  treasury: GoldSignalInputsTreasury;
  params_source: 'redis' | 'defaults';
  computed_at_utc: string;
}

export interface GoldSignalResponse {
  fund: 'GOLD';
  available?: boolean;      // false when the key is missing; absent when populated
  timestamp?: string;       // producer's now_utc.isoformat()
  decision?: 'BUY' | 'SELL' | 'HOLD';
  position_proxy?: 'BUY' | 'SELL' | null;
  degraded?: boolean;
  reason?: string;
  m_1040?: number | null;              // decimal fraction, not bps
  decision_price?: number | null;
  decision_price_ts_utc?: string | null;
  anchor_price?: number | null;
  anchor_price_ts_utc?: string | null;
  m_stale?: boolean;
  m_reason?: string;
  dy20_bps?: number | null;
  y10_latest?: number | null;
  y10_anchor?: number | null;
  y10_latest_date?: string | null;
  y10_anchor_date?: string | null;
  yield_degraded?: boolean;
  yield_reason?: string;
  gate_mode?: string;
  gate_open?: boolean;
  gate_reason?: string;
  threshold_bps?: number | null;
  threshold_reason?: string;
  params_echo?: GoldSignalParamsEcho;
  inputs?: GoldSignalInputs;
  preview?: GoldSignalPreview | null;  // live-ticking display replica; null when absent/expired
}

// Live-ticking display preview (2026-08-17): the python producer's
// latest:signal:gold:preview payload, attached by the API under `preview`.
// Recomputed every ~60s with a 600s TTL — the 08:40 stamped decision above
// remains the only authoritative signal; this is display-only.
export interface GoldSignalPreview {
  kind: 'preview';
  fund: 'GOLD';
  timestamp?: string;
  decision?: 'BUY' | 'SELL' | 'HOLD';
  degraded?: boolean;
  reason?: string;
  m_1040?: number | null;              // decimal fraction, not bps
  decision_price?: number | null;
  decision_price_ts_utc?: string | null;
  anchor_price?: number | null;
  anchor_price_ts_utc?: string | null;
  m_stale?: boolean;
  m_reason?: string;
  dy20_bps?: number | null;
  yield_degraded?: boolean;
  gate_mode?: string;
  gate_open?: boolean;
  gate_reason?: string;
  threshold_bps?: number | null;
  threshold_reason?: string;
  position_proxy?: 'BUY' | 'SELL' | null;
  last_bar_age_min?: number | null;
}

// GGE promotion Phase 4 (2026-08-18): /api/signal/gge — the gge_signal
// producer's latest:signal:gge payload forwarded verbatim (see
// src/signal_core/gge_mt_signal.py build_payload). The gold contract minus
// the yield gate (the GGE Phase-1 verdict validated the tune without one):
// m_0840 is CL (WTI) momentum from the prior 16:00 America/New_York close
// to the last print before 08:40 London, as a decimal fraction.
export interface GgeSignalParamsEcho {
  step_aside_threshold_bps?: number;
  step_aside_buy_threshold_bps?: number | null;
  step_aside_reentry_bps?: number | null;
}

export interface GgeSignalInputsClBars {
  last_bar_utc: string | null;
  bar_count: number;
  age_min: number | null;
}

export interface GgeSignalInputs {
  cl_bars: GgeSignalInputsClBars;
  params_source: 'redis' | 'defaults';
  computed_at_utc: string;
}

export interface GgeSignalResponse {
  fund: 'GGE';
  available?: boolean;      // false when the key is missing; absent when populated
  timestamp?: string;
  decision?: 'BUY' | 'SELL' | 'HOLD';
  position_proxy?: 'BUY' | 'SELL' | null;
  degraded?: boolean;
  reason?: string;
  m_0840?: number | null;              // decimal fraction, not bps
  decision_price?: number | null;
  decision_price_ts_utc?: string | null;
  anchor_price?: number | null;
  anchor_price_ts_utc?: string | null;
  m_stale?: boolean;
  // Also carries the crude contract-roll guard's reasons (2026-10-03):
  // "contract_roll" / "contract_unknown", alongside the pre-existing
  // staleness reasons — the signal panel surfaces these as an explainer
  // pill next to the degraded badge.
  m_reason?: string | null;
  threshold_bps?: number | null;
  threshold_reason?: string;
  params_echo?: GgeSignalParamsEcho;
  inputs?: GgeSignalInputs;
  preview?: GgeSignalPreview | null;   // live-ticking display replica; null when absent/expired
}

// Live-ticking display preview — same contract as GoldSignalPreview: the
// producer republishes every ~60s with a 600s TTL; the 08:40 stamp above
// is the only authoritative signal.
export interface GgeSignalPreview {
  kind: 'preview';
  fund: 'GGE';
  timestamp?: string;
  decision?: 'BUY' | 'SELL' | 'HOLD';
  degraded?: boolean;
  reason?: string;
  m_0840?: number | null;              // decimal fraction, not bps
  decision_price?: number | null;
  decision_price_ts_utc?: string | null;
  anchor_price?: number | null;
  anchor_price_ts_utc?: string | null;
  m_stale?: boolean;
  m_reason?: string;
  threshold_bps?: number | null;
  threshold_reason?: string;
  position_proxy?: 'BUY' | 'SELL' | null;
  last_bar_age_min?: number | null;
}

// /api/nav/{bga|jh|gold|gge} shared shape. price is in the fund's own NAV
// unit — pounds for BGA, PENCE for JH/GOLD/GGE (see nav/fundConfig.ts).
export interface FundNavLatest {
  available: boolean;
  date?: string;
  price?: number;
}

// GGE-vs-CL chart snapshots (promotion Phase 5 — 2026-08-19). CL is the
// intraday price line (WTI front-month, GLBX.MDP3 CL.n.0, downsampled to
// bucketMinutes); ggeNav is the daily-cadence NAV series (HL factsheet
// pence). See /api/charts/gge-cl-snapshots.
export interface GgeClSnapshotsResponse {
  available: boolean;
  window: '24h' | '5d' | '30d';
  bucketMinutes: number;
  contract: string;   // "CL.n.0" today
  clBars: { ts_utc: string; close: number }[];
  ggeNav: { date: string; pence: number }[];
}

// P&L performance charts (spec 2026-09-20-pnl-performance-charts-design.md).
// Shared wire shape of GET /api/user/subledger/performance (booked, per
// user) and GET /api/nav-history/{fund} (official NAV). pct is percent
// change from anchorDate; the anchor itself is the first point at 0.
export type PerformanceWindow = '1w' | '10d' | '1m' | '3m' | '6m' | '9m' | 'ytd';
export type PerformanceFund = PotFund | 'TOTAL';
export interface PerformancePoint { date: string; pct: number; live: boolean }
export interface PerformanceSeriesResponse {
  fund: string;
  window: PerformanceWindow;
  anchorDate: string | null;
  inception: string;
  clippedToInception: boolean;
  liveAsOf: string | null;
  points: PerformancePoint[];
}

// Japan fund page UI Task 3 (spec 2026-09-28-japan-fund-page-ui): the
// L&G Japan Index signal/market-data contracts. JAPAN is a dark,
// entitlement-gated fund (see nav/fundConfig.ts FUND_REGISTRY.japan) — its
// decision strikes at 08:40 UK off NIY futures momentum, mirroring the
// gold/GGE 08:40/12:00 signal shape minus the fields those funds don't
// share (no yield gate; decision is BUY/SELL only, no HOLD).
// Generalised 2026-10-07 (Charteris Phase 2.5, Task 4): shared shape for
// any 08:40-decision-cutoff gated-futures fund signal (JAPAN's NIY today,
// CHARTERIS's SI from Task 5 on). `fund` is widened to `string` here and
// narrowed back to the literal on JapanSignal below so existing JAPAN call
// sites keep their exact type.
export interface GatedFuturesSignal {
  fund: string;
  available?: boolean;
  timestamp?: string;
  decision?: 'BUY' | 'SELL';
  position_proxy?: string;
  degraded?: boolean;
  reason?: string;
  tsx_hold?: boolean;
  m_0840?: number | null;
  decision_price?: number | null;
  decision_price_ts_utc?: string | null;
  anchor_price?: number | null;
  anchor_price_ts_utc?: string | null;
  anchor_session?: string | null;
  m_stale?: boolean;
  m_reason?: string | null;
  threshold_bps?: number | null;
  threshold_reason?: string | null;
}

export interface JapanSignal extends GatedFuturesSignal {
  fund: 'JAPAN';
}

// Charteris Phase 2.5 Task 5: GET /api/signal?fund=charteris — the
// charteris_mt_signal producer payload verbatim (SI move since the NY
// close, 08:40 London decision), or `{ fund, available: false }` before the
// first publish. `tsx_hold: true` = TSX holiday on the anchor session,
// yesterday's decision carried (reason 'tsx_holiday', not degraded).
export interface CharterisSignal extends GatedFuturesSignal {
  fund: 'CHARTERIS';
}

// Copper fund page (AMATI Phase 2.5 lean, 2026-10-11): GET /api/signal?fund=amati —
// the amati_mt_signal producer payload verbatim (COMEX copper HG move since the
// previous 16:00 New York close, 08:40 London decision, BUY/SELL only, no TSX
// hold), or `{ fund, available: false }` before the first publish. 404 unless
// the caller may view AMATI.
export interface AmatiSignal extends GatedFuturesSignal {
  fund: 'AMATI';
}

// Charteris Phase 2.5 Task 6: GET /api/admin/charteris/status — read-only
// superuser summary (CharterisAdminStatusEndpoints.cs). Superuser-only;
// 404 for everyone else (never surfaced to a CHARTERIS-entitled non-superuser).
export interface CharterisAdminStatusEvent {
  atUtc: string;
  message: string;
}
export interface CharterisAdminStatusResponse {
  siTickerSwitch: 'enabled' | 'disabled' | 'unknown';
  capacity: CharterisAdminStatusEvent | null;
  levyWatch: CharterisAdminStatusEvent | null;
  navCaptureGaveUp: CharterisAdminStatusEvent | null;
  latestNav: { date: string; navPence: number } | null;
  signalAtUtc: string | null;
}

// 2026-10-07 (Paolo): Charteris (silver) mini charts — CharterisChartEndpoints.cs.
export interface CharterisIntradayRow { tsUtc: string; si: number | null; gc: number | null }
export interface CharterisIntradayResponse {
  window: string
  anchorTsUtc: string
  siAnchor: number | null
  gcAnchor: number | null
  rows: CharterisIntradayRow[]
}
// 2026-10-09: Charteris live K_t (plan 2026-10-09 Phase 3); generalised 2026-10-15 (the fund-generic
// LiveSynthNavPublisherService, CHARTERIS and AMATI) — minute rows of {fund}_kt_intraday and the latest
// synth_nav:{fund}:live document. Both 404 unless the caller may view the fund.
export type LiveSynthFund = 'charteris' | 'amati'
export interface LiveKtIntradayRow { ts_utc: string; synth_nav_pence: number; return_pct: number; tier: string }
export interface LiveKtIntradayResponse { window: string; rows: LiveKtIntradayRow[] }
// stale_pct (2026-10-10): quotes older than 45 min (a last close), split out of delayed_pct; optional so a payload from
// an older API still renders. flat_pct (2026-10-15, Amati): cash / warrant marks held flat; absent for Charteris.
export interface LiveKtCoverage { live_pct: number; delayed_pct: number; stale_pct?: number; proxy_pct: number; uncovered_pct: number; flat_pct?: number }
export interface LiveKtHolding {
  name: string
  symbol: string | null
  weight_pct: number
  state: 'live' | 'delayed' | 'stale' | 'proxy' | 'suspect' | 'uncovered' | string
  price: number | null
  return_pct: number
  contribution_bp: number
  beta: number
  /** Charteris: the holding's silver share m. Absent for Amati. */
  m?: number | null
  /** Amati: the future the holding rolls on (GC / SI / HG); absent = no roll (carried). */
  driver?: string | null
}
export interface LiveSynthNav {
  anchor_date: string
  anchor_pence: number
  synth_nav_pence: number
  return_pct: number
  /** live | degraded | critical (feed fault, return zeroed) | closed (Globex metals shut, last-close estimate kept; 2026-10-10). */
  tier: string
  ts_utc: string
  /** Charteris: the calibrated betas' quarter. */
  betas_quarter?: string
  /** Amati v1: 'uncalibrated_v1' (every beta 1.0). */
  betas?: string
  coverage: LiveKtCoverage
  holdings: LiveKtHolding[]
}
// Charteris names kept as aliases of the generic types.
export type CharterisKtIntradayRow = LiveKtIntradayRow
export type CharterisKtIntradayResponse = LiveKtIntradayResponse
export type CharterisKtCoverage = LiveKtCoverage
export type CharterisKtHolding = LiveKtHolding
export type CharterisSynthNavLive = LiveSynthNav
export interface CharterisKtAccuracy {
  last_date: string | null
  rmse_30_bp: number
  rmse_all_bp: number
  mean_bp: number
  days: number
  recent: { date: string; err_bp: number }[]
}
export interface CharterisDailyRow {
  date: string
  navPence: number
  siClose: number | null
  gcClose: number | null
  gbpUsd: number | null
  siGbp: number | null
  gcGbp: number | null
  gapBp: number | null
}
export interface CharterisDailyResponse { window: string; rows: CharterisDailyRow[] }

// 2026-10-06 (Japan mini charts spec): GET /api/japan/charts/intraday — 5-minute shared snapshots.
export interface JapanIntradayRow {
  tsUtc: string;
  synthNavPence: number | null;
  returnFrac: number | null;
  phase: string | null;
  equitySource: string | null;
  niyPrice: number | null;
  gbpJpyMid: number | null;
  niyGbp: number | null;
  anchorDate: string | null;
  anchorNavPence: number | null;
}
export interface JapanIntradayResponse {
  window: string;
  officialNav: { date: string; pence: number } | null;
  rows: JapanIntradayRow[];
}
// GET /api/japan/charts/topix — per official NAV date.
export interface JapanTopixRow {
  date: string;
  navPence: number;
  topixClose: number | null;
  gbpJpy1200: number | null;
  topixGbp: number | null;
  noonSynthErrorBp: number | null;
}
export interface JapanTopixResponse { window: string; rows: JapanTopixRow[] }

// NIY (Nikkei 225 futures) intraday bar — GET /api/japan/niy-intraday.
export interface NiyBar {
  tsUtc: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

// NIY daily close — GET /api/japan/niy-daily.
export interface NiyDailyRow {
  date: string;
  close: number;
}

// L&G Japan Index ex-dividend row — GET /api/japan/distributions.
export interface DistributionRow {
  xdDate: string;
  amountPence: number;
}

// Annualised metrics of a shared-pot curve (C# SharedPotMetrics) — used by the FOF backtest.
export interface SharedPotMetrics { cagrPct: number; sharpe: number; maxDdPct: number; days: number }

// Spec 2026-10-12: FOF backtest (/api/fof/backtest). Mirrors FofBacktestReport / FofBacktestResponse (C#).
export interface PotRow { fundId: string; enabled: boolean; weightPct: number; tier: number | null; reservePct: number }
export interface FofGates { dsr: boolean; cpcv: boolean; paired: boolean; pbo: boolean | null }
export interface FofTableResult {
  id: string; kind: 'Live' | 'Candidate' | 'Grid'; label: string; rows: PotRow[]; changedFields: number
  full: SharedPotMetrics; oos: SharedPotMetrics; deals: number; meanUtilisationPct: number
  /** Same metrics over the last 250 sessions of the same run (2026-10-31); absent on older reports. */
  recent?: SharedPotMetrics | null
  cpcvMedianSharpe: number; cpcvMinSharpe: number; cpcvPositive: number
  dsr: number; dsrPValue: number; deltaSharpe: number | null; deltaLo: number | null; deltaHi: number | null
  gates: FofGates; fitForPromotion: boolean; rank: number; error: string | null; equity: number[] | null
  /** Per-fund contribution inside the pot (2026-10-13): Live and Candidate only; absent/null on grid cells and older reports. */
  funds?: SharedPotFundDetail[] | null
}
/** One fund inside the shared pot (C# SharedPotFundDetail). shareOfPotPct is null when pot P&L <= 0. */
export interface SharedPotFundDetail {
  fund: string; label: string; targetWeightPct: number; contributionGbp: number
  shareOfPotPct: number | null; trades: number; daysHeld: number
  dealingCostsGbp: number; platformFeesGbp: number; distributionsGbp: number
  byYearGbp: Record<string, number>; cumulativeContributionGbp: number[]; deals: SharedPotFundDeal[]
}
export interface SharedPotFundDeal { date: string; kind: string; amountGbp: number }
export interface FofPresetInfo { fundId: string; presetName: string; paramsHash: string }
export interface FofBacktestReport {
  generatedAtUtc: string; spanStart: string; spanEnd: string; oosFrom: string; dates: string[]
  inputsKey: string; trialCount: number; pbo: number | null; pboMedianLogit: number | null; gridMode: string
  presets: FofPresetInfo[]; liveRows: PotRow[]; tables: FofTableResult[]
  /** What stops the history (2026-10-14); absent on reports from older runs. */
  spanLimit?: FofSpanLimit | null
}
export interface FofSpanLimit {
  fundEnds: { fundId: string; lastDate: string }[]
  limitedBy: string[]
  inputs: { fundId: string; file: string; lastDate: string }[]
  /** Inputs ending before the span end — their missing days read as zero or blank. */
  lagging?: { fundId: string; file: string; lastDate: string }[]
  weekdaysBehind: number; stale: boolean
}
export interface FofBacktestResponse {
  latest: { jobId: string; createdAt: string; completedAt: string | null; elapsedMs: number; report: FofBacktestReport } | null
  inFlight: { jobId: string; status: string; phase: string; createdAt: string } | null
  lastFailure: { jobId: string; error: string; completedAt: string | null } | null
  canRun: boolean; liveRows: PotRow[]; presetsNow: FofPresetInfo[]; stale: boolean; promotedJobId: string | null
  /** Null when no run exists or the mechanism read failed (Promote re-checks either way). */
  mechanism?: FofMechanismView | null
}
// Spec 2026-10-31: mechanism read next to the backtest (C# FofMechanismView). Figures are % per year from each fund's
// latest 250-day edge-source point; a table may not raise a fund that is not eligible.
export interface FofFundEligibility {
  fundId: string; eligible: boolean; reason: string | null; rag: string | null
  stalePctYr: number | null; continuationPctYr: number | null; otherPctYr: number | null
}
export interface FofPromoteBlock { fundId: string; reason: string }
export interface FofTableMechanism {
  tableId: string; stalePctYr: number; continuationPctYr: number; otherPctYr: number
  deltaStalePctYr: number; deltaContinuationPctYr: number; deltaOtherPctYr: number
  coveredPct: number; trendPaid: boolean; blocks: FofPromoteBlock[]
}
export interface FofMechanismView { edgeAsOf: string | null; ragAvailable: boolean; funds: FofFundEligibility[]; tables: FofTableMechanism[] }
export interface FofGridSpec { mode: 'oat' | 'pair' | 'none'; funds?: string[] }
/** FOF home page row (spec 2026-10-12 §4.2) — one per fund the caller can see. */
// FOF home redesign (spec 2026-11-02 §5): one mini-chart block — signed % over the window and the ≤60-point path.
// markAt (2026-11-03): fraction of the x-axis (0..1) where the block's marked instant falls (the decision cutoff on
// the overnight chart, today's strike on the anchored / K_t charts), null until that instant is inside the path.
// markValue: the series value at that instant — the base of the anchored / K_t % (Paolo 2026-11-03: "% since the last strike").
// closeAt: fraction of the anchor exchange's latest close (the US EOD most NAVs are valued against) — a dark-grey line.
export interface FofSeries {
  pct: number | null; series: number[]; markAt: number | null; markValue: number | null; closeAt: number | null
  // 2026-11-03 (K_t): the revaluation step on the strike bar once the official NAV has landed (from the synth at the
  // strike to the official NAV), and the window's first/last instants so the zoomed chart can label the time.
  stepFrom?: number | null; stepTo?: number | null; startUtc?: string | null; endUtc?: string | null
  // 2026-11-03 (two-chart row): the 08:40 decision bar and the values at the exchange close / the decision, for the
  // section colouring (start→close, close→decision = ON, decision→strike, strike→now) and the ON % beside its bar.
  decisionAt?: number | null; decisionValue?: number | null; closeValue?: number | null
}
export interface FofDealtToday { direction: 'BUY' | 'SELL'; notionalGbp: number | null }
export interface FofFundRow {
  fund: string; slug: string; label: string
  futuresSymbol: string | null; futuresMovePct: number | null
  fundMovePct: number | null; fundMoveSource: 'est' | 'nav' | null; navDate: string | null
  decision: string | null; signalDate: string | null
  invested: boolean | null; positionGbp: number | null
  health: SignalHealthStatus | null; healthReason: string | null; healthSummary: string | null
  // 2026-11-02: futures since its previous exchange close / since the fund's strike anchor; K_t synth NAV since the
  // anchor; the strike time ("10:00" BGA, "12:00" others); live = weight > 0 in the viewer's pot; today's deal.
  overnight: FofSeries | null; anchored: FofSeries | null; kt: FofSeries | null
  anchorTimeUk: string; live: boolean | null; dealtToday: FofDealtToday | null
}
export interface FofOverview { asOfUtc: string; funds: FofFundRow[] }

export const api = {
  status: () => request<{ loggedIn: boolean }>('/api/status'),
  accounts: (fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/accounts?fund=${fund.toLowerCase()}` : '/api/accounts';
    return request<HLAccount[]>(url);
  },
  position: (sedol: string, accountId: number) =>
    request<FundPosition>(`/api/position/${sedol}/${accountId}`),
  buy: (sedol: string, accountId: number, amount: number) =>
    request<DealConfirmation>('/api/buy', {
      method: 'POST',
      body: JSON.stringify({ sedol, accountId, amount }),
    }),
  sell: (sedol: string, accountId: number, opts: { amount?: number; units?: number; sellAll?: boolean }) =>
    request<DealConfirmation>('/api/sell', {
      method: 'POST',
      body: JSON.stringify({ sedol, accountId, ...opts }),
    }),
  health: () => fetch('/healthz', { credentials: 'include' }).then(r => r.ok),
  alerts: () => request<{ alerts: Array<{ type: string; message: string }> }>('/api/alerts'),
  signal: (fund?: Fund) => {
    const url = fund ? `/api/signal?fund=${fund.toLowerCase()}` : '/api/signal';
    return request<SignalData>(url);
  },
  mlPrediction: () => request<MlPredictionData>('/api/ml-prediction'),
  prices: () => request<Record<string, PriceData>>('/api/prices'),
  holdingsStatus: (fund?: 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'OTP') => {
    const url = fund ? `/api/holdings/status?fund=${fund.toLowerCase()}` : '/api/holdings/status';
    return request<HoldingsStatusData>(url);
  },
  holdingsActive: (fund?: 'BGA' | 'JH' | 'GGE') => {
    const url = fund ? `/api/holdings/active?fund=${fund.toLowerCase()}` : '/api/holdings/active';
    return request<HoldingsActiveEntry[]>(url);
  },
  synthNav: () => request<SynthNavData>('/api/synth-nav'),
  enrichedSynthNav: (fund?: Fund) => {
    const qs = fund ? `?fund=${fund.toLowerCase()}` : ''
    return request<EnrichedSynthNavData>(`/api/synth-nav/enriched${qs}`)
  },
  goldNavSummary: () => request<GoldNavSummary>('/api/gold/nav-summary'),
  goldHoldings: () => request<GoldHoldingsResponse>('/api/gold/holdings'),
  goldVolRegime: () => request<GoldVolRegimeResponse>('/api/gold/vol-regime'),
  goldSynthNav: () => request<GoldSynthNavResponse>('/api/gold/synth-nav'),
  /**
   * GOLD-vs-GC 15-minute candles (V108). Drives GoldVsGcCandleChart.vue,
   * which the gold sparkline's GC item links to. Server-composed from
   * synth_nav:gold:live (GBP pence) + gold_intraday_1m GC.n.0 (USD/oz) +
   * latest:price:GBPUSD (drives the ccy-adjusted toggle).
   */
  chartGoldFuturesSnapshots: (windowSpec: '24h' | '5d' | '7d' | '10d' | '20d' | '30d' | 'all' = '24h') =>
    request<GoldFuturesSnapshotResponse>(`/api/charts/gold-futures-snapshots?window=${windowSpec}`),
  // V109 GGE-vs-CL — mirror of chartGoldFuturesSnapshots. Distinct URL
  // from ggeClSnapshots above (that one is the older sparkline/mini
  // shape: CL line + daily GGE NAV bars).
  chartGgeClCandles: (windowSpec: '24h' | '5d' | '7d' | '10d' | '20d' | '30d' | 'all' = '24h') =>
    request<GgeClSnapshotResponse>(`/api/charts/gge-cl-candles?window=${windowSpec}`),
  // 2026-10-10 CHARTERIS-vs-SI — mirror of chartGgeClCandles behind the Charteris gate (CharterisChartEndpoints.cs).
  chartCharterisSiCandles: (windowSpec: '24h' | '5d' | '7d' | '10d' | '20d' | '30d' | 'all' = '24h') =>
    request<CharterisSiSnapshotResponse>(`/api/japan/charts/charteris-si-candles?fund=CHARTERIS&window=${windowSpec}`),
  goldSynthNavLive: async (): Promise<GoldSynthNavLive | null> => {
    const res = await fetch(`${BASE}/api/gold/synth-nav-live`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`GET /api/gold/synth-nav-live failed: ${res.status}`);
    const raw = await res.json();
    return {
      anchorPence: raw.anchor_pence,
      anchorKind: raw.anchor_kind,
      anchorAsOfUk: raw.anchor_as_of_uk,
      liveReturnGbp: raw.live_return_gbp,
      synthNavPence: raw.synth_nav_pence,
      tsUtc: raw.ts_utc,
      tier: raw.tier,
      coveredWeightFrac: raw.covered_weight_frac,
      gcDriftPct: raw.gc_drift_pct,
      gcLastTickUtc: raw.gc_last_tick_utc,
      perHolding: (raw.per_holding ?? []).map((p: any) => ({
        ticker: p.ticker,
        weight: p.weight,
        beta: p.beta,
        mktReturnGbp: p.mkt_return_gbp,
        state: p.state,
      })),
    };
  },
  // Spec 2026-10-04: 404 (no value yet / not viewable) ⇒ null, like ggeSynthNavLive.
  japanSynthNavLive: async (): Promise<JapanSynthNavLive | null> => {
    const res = await fetch(`${BASE}/api/japan/synth-nav-live?fund=JAPAN`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`GET /api/japan/synth-nav-live failed: ${res.status}`)
    return await res.json() as JapanSynthNavLive
  },
  ggeSynthNavLive: async (): Promise<GgeSynthNavLive | null> => {
    const res = await fetch(`${BASE}/api/gge/synth-nav-live`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`GET /api/gge/synth-nav-live failed: ${res.status}`);
    const raw = await res.json();
    return {
      anchorPence: raw.anchor_pence,
      anchorKind: raw.anchor_kind,
      anchorAsOfUk: raw.anchor_as_of_uk,
      liveReturnGbp: raw.live_return_gbp,
      synthNavPence: raw.synth_nav_pence,
      tsUtc: raw.ts_utc,
      tier: raw.tier,
      coveredWeightFrac: raw.covered_weight_frac,
      clDriftPct: raw.cl_drift_pct,
      clLastTickUtc: raw.cl_last_tick_utc,
      method: raw.method,
      perHolding: (raw.per_holding ?? []).map((p: any) => ({
        ticker: p.ticker,
        weight: p.weight,
        beta: p.beta,
        mktReturnGbp: p.mkt_return_gbp,
      })),
    };
  },
  goldKtLatest: () => request<GoldKtLatestResponse>('/api/gold/kt-latest'),
  // Task 11 (2026-09-16 gold/GGE strike-anchor arc PR D) — see
  // StrikeAnchorFundHealth doc above.
  strikeAnchorHealth: () => request<StrikeAnchorHealthResponse>('/api/system/strike-anchor-health'),
  ktAttribution: (fund: 'bga' | 'jh', date?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (date) qs.set('date', date)
    if (limit != null) qs.set('limit', String(limit))
    const qsStr = qs.toString()
    return request<KtAttributionResponse>(`/api/synthetic/${fund}/kt-attribution${qsStr ? `?${qsStr}` : ''}`)
  },
  // See GgeKtLatestResponse doc above — backend route not yet registered
  // (NEEDS_CONTEXT, Task 21 report). Wired now so composable/component code
  // needs no further edit once it ships.
  ggeKtLatest: () => request<GgeKtLatestResponse>('/api/gge/kt-latest'),
  strikeTotal: (fund: 'gold' | 'gge' | 'bga' | 'jh') => request<StrikeTotalResponse>(`/api/${fund}/strike-total`),
  goldHoldingsLatest: () => request<GoldHoldingsLatestResponse>('/api/gold/holdings-latest'),
  goldXau: () => request<GoldXauResponse>('/api/gold/xauusd'),
  goldTreasuryYields: () => request<GoldTreasuryYieldsResponse>('/api/gold/treasury-yields'),
  // gold-productionization Task 10 — thin Redis read of latest:signal:gold
  goldSignal: () => request<GoldSignalResponse>('/api/signal/gold'),
  ggeSignal: () => request<GgeSignalResponse>('/api/signal/gge'),
  ggeNav: () => request<FundNavLatest>('/api/nav/gge'),
  // Japan fund page UI Task 3 — dark/gated fund, entitlement-checked by the
  // caller (useVisibleFunds) before these are ever invoked.
  japanSignal: () => request<JapanSignal>('/api/signal?fund=japan'),
  niyIntraday: () => request<NiyBar[]>('/api/japan/niy-intraday?fund=JAPAN'),
  japanChartsIntraday: (window: '1d' | '5d' | '1m' = '1d') =>
    request<JapanIntradayResponse>(`/api/japan/charts/intraday?fund=JAPAN&window=${window}`),
  japanChartsTopix: (window: '1m' | '3m' | '1y' | '5y' = '3m') =>
    request<JapanTopixResponse>(`/api/japan/charts/topix?fund=JAPAN&window=${window}`),
  niyDaily: (days?: number) =>
    request<NiyDailyRow[]>(`/api/japan/niy-daily?fund=JAPAN${days !== undefined ? `&days=${days}` : ''}`),
  japanDistributions: () => request<DistributionRow[]>('/api/japan/distributions?fund=JAPAN'),
  // Task 12: latest published L&G Japan Index C Inc NAV in PENCE (last row of
  // lg_japan_index_c_inc_nav.csv). fundNavHistory('JAPAN') is percent-change
  // only, so the Signal tab's NavSourceCard reads the level here. 404 when the
  // caller isn't entitled or no NAV has been written yet.
  japanNavLatest: () => request<{ date: string; navPence: number }>('/api/japan/nav-latest?fund=JAPAN'),
  // Charteris Phase 2.5 Task 5 — dark/gated fund; the server 404s both calls
  // for a caller who cannot view CHARTERIS. nav-latest reads
  // charteris_gold_pm_i_inc_nav.csv (Task 1 generalised the route).
  charterisSignal: () => request<CharterisSignal>('/api/signal?fund=charteris'),
  // Copper fund page (AMATI, gated like charteris): 404 unless the caller may view AMATI.
  amatiSignal: () => request<AmatiSignal>('/api/signal?fund=amati'),
  // Generic fund-pack sign-rule Signal tab (MINERS first): same payload shape as the per-fund calls above.
  gatedSignal: (fund: Fund) => request<GatedFuturesSignal>(`/api/signal?fund=${fund.toLowerCase()}`),
  charterisNavLatest: () => request<{ date: string; navPence: number }>('/api/japan/nav-latest?fund=CHARTERIS'),
  // Charteris Phase 2.5 Task 6 — superuser-only Admin tab status card. 404s for
  // anyone else (CharterisAdminStatusEndpoints.cs).
  charterisAdminStatus: () => request<CharterisAdminStatusResponse>('/api/admin/charteris/status'),
  charterisChartsIntraday: (window: '1d' | '5d') =>
    request<CharterisIntradayResponse>(`/api/japan/charts/charteris-intraday?fund=CHARTERIS&window=${window}`),
  charterisKtIntraday: (window: '1d' | '5d') =>
    request<LiveKtIntradayResponse>(liveKtIntradayUrl('charteris', window)),
  charterisSynthNavLive: () => request<LiveSynthNav>(liveSynthNavUrl('charteris')),
  /** Amati live tile (2026-10-15): the shared live K_t routes (LiveSynthNavEndpoints). */
  amatiSynthNavLive: () => request<LiveSynthNav>(liveSynthNavUrl('amati')),
  amatiKtIntraday: (window: '1d' | '5d') =>
    request<LiveKtIntradayResponse>(liveKtIntradayUrl('amati', window)),
  charterisKtAccuracy: () => request<CharterisKtAccuracy>('/api/charteris/kt-accuracy?fund=CHARTERIS'),
  charterisChartsDaily: (window: '1m' | '3m' | '1y' | '5y') =>
    request<CharterisDailyResponse>(`/api/japan/charts/charteris-daily?fund=CHARTERIS&window=${window}`),
  ggeClSnapshots: (window: '24h' | '5d' | '30d' = '24h') =>
    request<GgeClSnapshotsResponse>(`/api/charts/gge-cl-snapshots?window=${window}`),
  bookedPerformance: (fund: PerformanceFund, window: PerformanceWindow) =>
    request<PerformanceSeriesResponse>(`/api/user/subledger/performance?fund=${fund}&window=${window}`),
  // Accepts every PotFund (the funds with a NAV-priced share class): GET /api/nav-history/{fund}
  // runs on the same percent-change endpoint machinery for all of them, gated funds included.
  fundNavHistory: (fund: Exclude<PerformanceFund, 'TOTAL'>, window: PerformanceWindow) =>
    request<PerformanceSeriesResponse>(`/api/nav-history/${fund.toLowerCase()}?window=${window}`),
  refreshHoldings: (fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/holdings/refresh?fund=${fund.toLowerCase()}` : '/api/holdings/refresh';
    return request<{ triggered: boolean }>(url, { method: 'POST' });
  },
  equityPrices: () => request<EquityPricesResponse>('/api/equity-prices'),
  // No args = the Event Log's full window (server default 300 events / 144h);
  // narrow pollers pass maxHours (and/or count) to keep the payload small.
  events: (opts?: { count?: number; maxHours?: number }) => {
    const params = new URLSearchParams();
    if (opts?.count !== undefined) params.set('count', String(opts.count));
    if (opts?.maxHours !== undefined) params.set('maxHours', String(opts.maxHours));
    const q = params.toString() ? `?${params}` : '';
    return request<EventEntry[]>(`/api/events${q}`);
  },
  jhNav: () => request<JhNavResponse>('/api/jh/nav'),
  jhNavHistory: (window?: '1d' | '5d' | '7d' | '30d' | '90d' | '1y' | '5y' | 'all', ccyAdjusted?: boolean, compare?: 'bga') => {
    const params = new URLSearchParams();
    if (window) params.set('window', window);
    if (ccyAdjusted !== undefined) params.set('ccy_adjusted', String(ccyAdjusted));
    if (compare) params.set('compare', compare);
    const q = params.toString() ? `?${params}` : '';
    return request<JhNavHistoryResponse>(`/api/jh/nav-history${q}`);
  },
  logEvent: (type: string, message: string, detail?: string) =>
    fetch(`${BASE}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type, message, detail }),
    }).catch(() => {}),
  // Linked users Task 9 fix round 1 (reviewer, spec 2026-09-28): was a raw
  // fetch() in AccountPanel.vue that bypassed request() entirely, so it
  // never carried X-SideWinder-View-As — while viewing as, the Account
  // panel showed the SUPERUSER's own HL accounts/valuations next to the
  // TARGET's allocations. GET /api/accounts/valuations is user-scoped
  // (UserContext uc), so it must go through request() like every other
  // per-user call.
  accountValuations: () => request<AccountValuation[]>('/api/accounts/valuations'),
  // Per-user allocations
  userAllocations: (fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/user/allocations?fund=${fund.toLowerCase()}` : '/api/user/allocations';
    return request<UserAccountAllocation[]>(url);
  },
  userSaveAllocations: (allocations: SaveAllocationRequest[], fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/user/allocations?fund=${fund.toLowerCase()}` : '/api/user/allocations';
    return request<{ saved: boolean; count: number }>(url, {
      method: 'POST',
      body: JSON.stringify(allocations),
    });
  },
  // Backtest
  backtestRun: (type: string, parameters: BacktestParams, options?: {
    gridSpace?: Record<string, any>
    fitnessPreset?: string
    topN?: number
    trainDays?: number
    testDays?: number
    stepDays?: number
    cpcvN?: number
    cpcvK?: number
    cpcvEmbargoDays?: number
    tpeTrialBudget?: number
    sweepOverlaySc?: boolean
    overlaySweepBudget?: string
    assetId?: BacktestAssetId     // routes job to the correct IAssetProfile (Task 10: JAPAN = 'lg_japan_index')
  }) =>
    request<{ jobId: string }>('/api/backtest/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, parameters, ...options }),
    }),
  backtestJobs: (assetId?: BacktestAssetId) => {
    const q = assetId ? `?assetId=${assetId}` : '';
    return request<BacktestJob[]>(`/api/backtest/jobs${q}`);
  },
  backtestJob: (id: string) => request<BacktestJob>(`/api/backtest/jobs/${id}`),
  backtestCancelJob: (id: string) =>
    fetch(`/api/backtest/jobs/${id}`, { method: 'DELETE', credentials: 'include' }),
  // Config endpoints are fund-threaded (parity Phase 5): assetId 'jh'
  // routes to backtest_configs/jhgtl.json and per-fund Redis keys; 'gold'
  // routes to backtest_configs/gold.json; omitted or 'bga' keeps the
  // legacy BGA behaviour.
  backtestConfigs: (assetId?: BacktestConfigFund) => {
    const q = assetId ? `?fund=${assetId}` : '';
    return request<BacktestConfigStore>(`/api/backtest/configs${q}`);
  },
  // overwrite: saving over an EXISTING preset name is refused by the backend
  // (409, error contains "already exists") unless overwrite=true — the
  // 2026-08-12 pinned-preset drift guard. Callers should confirm with the
  // operator before retrying with overwrite.
  backtestSaveConfig: (name: string, parameters: BacktestParams, sourceJobId?: string,
    assetId?: BacktestConfigFund, overwrite?: boolean) => {
    const q = assetId ? `?fund=${assetId}` : '';
    return request<{ saved: boolean; overwrote?: boolean }>(`/api/backtest/configs${q}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, parameters, sourceJobId, overwrite: overwrite ?? false }),
    });
  },
  backtestDeleteConfig: (name: string, assetId?: BacktestConfigFund) => {
    const q = assetId ? `?fund=${assetId}` : '';
    return fetch(`/api/backtest/configs/${name}${q}`, { method: 'DELETE', credentials: 'include' });
  },
  // Heals a preset whose linked backtest_results/{jobId}.json was purged (or
  // was never generated for a programmatic save with source_job_id=null).
  // POST /api/backtest/configs/{name}/relink?fund=<>. Unlike backtestSaveConfig,
  // this endpoint updates ONLY source_job_id — source_metrics (promotion-time
  // baseline) is preserved. See memory feedback_preset_needs_source_job_id_for_graph.
  backtestRelinkConfig: (name: string, sourceJobId: string,
    assetId?: BacktestConfigFund) => {
    const q = assetId ? `?fund=${assetId}` : '';
    return request<{ relinked: boolean; fund: string }>(
      `/api/backtest/configs/${encodeURIComponent(name)}/relink${q}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceJobId }),
      });
  },
  // Task 10: 'otp' added to match the server's /schedule whitelist (BacktestPanel used to cast it in);
  // JAPAN is deliberately absent — no standalone Japan schedule.
  backtestSchedule: (cron: string, type: string, parameters: BacktestParams, assetId?: 'bga' | 'jh' | 'gold' | 'gge' | 'otp') =>
    request<{ scheduleId: string }>('/api/backtest/schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cron, type, parameters, assetId }),
    }),
  backtestSchedules: (assetId?: 'bga' | 'jh' | 'gold' | 'gge') => {
    const q = assetId ? `?assetId=${assetId}` : '';
    return request<ScheduleEntry[]>(`/api/backtest/schedules${q}`);
  },
  backtestDeleteSchedule: (id: string) =>
    fetch(`/api/backtest/schedules/${id}`, { method: 'DELETE', credentials: 'include' }),
  // Preset promotion to live (Task 6, gold panel + parity with BacktestPanel's
  // raw-fetch equivalent). POST /api/backtest/configs/active/{name}?fund=<>.
  backtestSetActive: (name: string, assetId?: 'bga' | 'jh' | 'gold' | 'gge' | 'otp') => {
    const q = assetId ? `?fund=${assetId}` : '';
    return request<{ activated: string }>(`/api/backtest/configs/active/${encodeURIComponent(name)}${q}`, {
      method: 'POST',
    });
  },
  // Per-user execution
  userExecutionConfig: (fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/user/execution/config?fund=${fund.toLowerCase()}` : '/api/user/execution/config';
    return request<UserExecutionConfig>(url);
  },
  userSaveExecutionConfig: (config: UserExecConfigRequest) =>
    request<{ saved: boolean }>('/api/user/execution/config', {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  userSetAutoCompound: (enabled: boolean) =>
    request<{ enabled: boolean }>('/api/user/execution/auto-compound', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
  userExecutionStatus: (fund?: Fund) => {
    const url = fund ? `/api/user/execution/status?fund=${fund.toLowerCase()}` : '/api/user/execution/status';
    return request<UserExecutionStatus>(url);
  },
  execDecisions: () => request<ExecFundDecision[]>('/api/user/exec/decisions'),
  // FOF home page (spec 2026-10-12): per-visible-fund futures move, fund move, signal, position.
  fofOverview: () => request<FofOverview>('/api/fof/overview'),
  fofBacktest: () => request<FofBacktestResponse>('/api/fof/backtest'),
  fofBacktestRun: (body: { candidate: PotRow[]; grid: FofGridSpec }) =>
    request<{ jobId: string; status: string; alreadyRunning: boolean }>('/api/fof/backtest/run', {
      method: 'POST', body: JSON.stringify(body) }),
  fofBacktestPromote: (body: { jobId: string; table: string; acknowledgeFailedGates: boolean }) =>
    request<unknown>('/api/fof/backtest/promote', {
      method: 'POST', body: JSON.stringify(body) }),
  fofBacktestRevert: (body: { jobId: string }) =>
    request<unknown>('/api/fof/backtest/revert', {
      method: 'POST', body: JSON.stringify(body) }),
  fofBacktestCancel: () =>
    request<{ jobId: string }>('/api/fof/backtest/cancel', { method: 'POST' }),
  // OTP/BGA divorce Phase 1, Task 4: per-user residual-cash breakdown
  // backing ExecPanel's "OTP sleeve — residual-funded" row.
  otpResidualStatus: () => request<OtpResidualStatus>('/api/exec/otp-residual'),
  // Multi-strategy allocation (spec 2026-07-26-multi-strategy-allocation-
  // design.md §6): per-user ON/OFF + weight% across BGA/JH/GOLD, backing
  // StrategyAllocationCard.
  getFundAllocations: () => request<FundAllocationsResponse>('/api/user/fund-allocations'),
  // AllocationHero (2026-09-05): one coherent snapshot — the caller's four
  // OEIC allocation rows with latest position values, account cash, and the
  // dated post-09:00 OTP residual. Backs the Strategy tab hero's fill
  // fractions and OTP CASH chip.
  allocationStatus: () => request<AllocationStatus>('/api/user/allocation-status'),
  // Target-weight rebalancing plan Task 5: read-only view of the 08:40 UK
  // dealing plan, backing DealingPlanCard.
  getDealingPlan: (date?: string) =>
    request<DealingPlanView>(date ? `/api/user/dealing-plan?date=${date}` : '/api/user/dealing-plan'),
  putFundAllocations: (allocations: FundAllocation[]) =>
    request<FundAllocationsResponse>('/api/user/fund-allocations', {
      method: 'PUT',
      body: JSON.stringify({ allocations }),
    }),
  getGoldRampStatus: () => request<GoldRampStatus>('/api/gold/ramp-status'),
  // GGE sibling — same response shape (GoldRampStatus is the shared
  // {promoted, shadowDays, holdingsSnapshotAgeDays, runbook} contract).
  getGgeRampStatus: () => request<GoldRampStatus>('/api/gge/ramp-status'),
  // Fund-generic manual "sell everything" — StrategyAllocationCard's
  // per-fund liquidate button (ExecPanel's Manual Execute card was removed
  // 2026-08-12; this is the only manual-sell surface left in the UI).
  liquidateFund: (fund: Fund) =>
    request<LiquidateFundResponse>(`/api/user/funds/${fund.toLowerCase()}/liquidate`, {
      method: 'POST',
    }),
  userOtpConfig: (fund?: 'BGA' | 'JH' | 'OTP') => {
    const url = fund ? `/api/user/otp?fund=${fund.toLowerCase()}` : '/api/user/otp';
    return request<UserOtpConfig[]>(url);
  },
  userOtpToggle: (otpType: string) => request<{ toggled: string }>(`/api/user/otp/${otpType}/toggle`, { method: 'PUT' }),
  userOtpSave: (cfg: { otpType: string; stopLossPct: number; cooldownEnabled: boolean; cooldownDays: number }) =>
    request<{ saved: boolean }>(`/api/user/otp/${cfg.otpType}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ otpType: cfg.otpType, stopLossPct: cfg.stopLossPct, cooldownEnabled: cfg.cooldownEnabled, cooldownDays: cfg.cooldownDays }),
    }),
  vixIntraday: () => request<VixIntraday>('/api/vix/intraday'),
  alphaVantageHealth: () => request<AlphaVantageHealth>('/api/integrity/alphavantage'),
  bgEffectiveDateHealth: () => request<BgEffectiveDateHealth>('/api/integrity/bg-effective-date'),
  bgaTradeCoherence: () => request<BgaTradeCoherence>('/api/integrity/bga-trade-coherence'),
  databentoEquityHealth: () => request<DatabentoEquityHealth>('/api/integrity/databento-equity'),
  // Task 11 (JH K_t live-parity plan): optional fund param so the JH
  // dashboard can fetch its OWN pipeline-health probe
  // (kt-intraday-live?fund=jh) instead of always reading BGA's.
  ktIntradayLiveHealth: (fund?: 'BGA' | 'JH') => {
    const url = fund ? `/api/integrity/kt-intraday-live?fund=${fund.toLowerCase()}` : '/api/integrity/kt-intraday-live';
    return request<KtIntradayLiveHealth>(url);
  },
  imrWarmupHealth: () => request<ImrWarmupHealth>('/api/integrity/imr-warmup'),
  dataCsvFreshness: () => request<DataCsvFreshness>('/api/integrity/data-csv-freshness'),
  navCrossChecks: () => request<NavCrossChecks>('/api/integrity/nav-cross-checks'),
  bgaCusumLabels: (p?: Partial<BgaCusumParams>) => {
    const q = new URLSearchParams();
    if (p?.sigmaSpan != null) q.set('sigmaSpan', String(p.sigmaSpan));
    if (p?.kH != null) q.set('kH', String(p.kH));
    if (p?.kUp != null) q.set('kUp', String(p.kUp));
    if (p?.kDn != null) q.set('kDn', String(p.kDn));
    if (p?.horizonDays != null) q.set('horizonDays', String(p.horizonDays));
    const qs = q.toString();
    return request<BgaCusumResponse>(`/api/cusum/bga-labels${qs ? '?' + qs : ''}`);
  },
  calendarToday: () => request<CalendarStatus>('/api/calendar/today'),
  calendarHolidays: (fund?: string, year?: number) => {
    const qs = new URLSearchParams()
    if (fund) qs.set('fund', fund)
    if (year) qs.set('year', String(year))
    const s = qs.toString()
    return request<FundHolidayRow[]>(`/api/calendar/holidays${s ? '?' + s : ''}`)
  },
  // Per-user subledger
  userBalanceSheet: (date?: string, fund?: Fund) => {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (fund) q.set('fund', fund.toLowerCase());
    const qs = q.toString();
    return request<BalanceSheetData>(`/api/user/subledger/balance-sheet${qs ? `?${qs}` : ''}`);
  },
  userDailyPnl: (date?: string, fund?: Fund) => {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (fund) q.set('fund', fund.toLowerCase());
    const qs = q.toString();
    return request<DailyPnlData>(`/api/user/subledger/daily-pnl${qs ? `?${qs}` : ''}`);
  },
  userNavDeltas: (from?: string, to?: string, fund?: Fund) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    if (fund) q.set('fund', fund.toLowerCase());
    const qs = q.toString();
    return request<NavDeltaData[]>(`/api/user/subledger/nav-deltas${qs ? `?${qs}` : ''}`);
  },
  /**
   * de Prado ETF Trick synthetic K_t for the BGA basket. Default range is the
   * latest 90 trading days. See `EtfTrickKtData` for shape + dating semantics.
   */
  bgaKt: (from?: string, to?: string) => {
    const qs = [from ? `from=${from}` : '', to ? `to=${to}` : '']
      .filter(Boolean).join('&')
    return request<EtfTrickKtData[]>(`/api/synthetic/bga/kt${qs ? `?${qs}` : ''}`)
  },
  /**
   * Intraday K_t — minute-resolution counterpart of `bgaKt`. Default window
   * is the trailing 24h. Use ISO-8601 UTC strings (`2026-05-04T13:30:00Z`).
   */
  bgaKtIntraday: (fromUtc?: string, toUtc?: string) => {
    const qs = [fromUtc ? `fromUtc=${encodeURIComponent(fromUtc)}` : '',
                toUtc ? `toUtc=${encodeURIComponent(toUtc)}` : '']
      .filter(Boolean).join('&')
    return request<BasketKtIntradayData[]>(`/api/synthetic/bga/kt-intraday${qs ? `?${qs}` : ''}`)
  },
  /**
   * K_t expressed in NAV pence right now. Composes latest intraday K_t
   * (100-base index) and the most recent struck BGA NAV:
   *   K_now_pence = NAV_anchor × K_intraday(now) / K_intraday(at_strike)
   * Returns `{available: false, ...flags}` when inputs missing — caller
   * should render a "data unavailable" state with the per-input flags.
   * See KtNowPenceCalculator.cs for the full rationale.
   */
  bgaKtNowPence: () => request<KtNowPenceData>('/api/synthetic/bga/kt-now-pence'),
  getSwingWatch: () => request<SwingWatchResponse>('/api/swing-watch'),
  /**
   * Fund-parameterised counterpart of `bgaKt` (JH K_t live-parity plan
   * Task 10). Same shape/dating convention as `EtfTrickKtData`; `bgaKt()`
   * above stays as the BGA-locked convenience alias (still used by
   * Dashboard.vue's mini sparkline) — fund-aware chart components (e.g.
   * `BgaKtChart` with a `fund` prop) call this instead.
   */
  getSyntheticKt: (fund: 'bga' | 'jh' | 'gold' | 'gge', from?: string, to?: string) => {
    const qs = [from ? `from=${from}` : '', to ? `to=${to}` : '']
      .filter(Boolean).join('&')
    return request<EtfTrickKtData[]>(`/api/synthetic/${fund}/kt${qs ? `?${qs}` : ''}`)
  },
  /** Fund-parameterised counterpart of `bgaKtIntraday` — see `getSyntheticKt`. */
  getSyntheticKtIntraday: (fund: 'bga' | 'jh' | 'gold' | 'gge', fromUtc?: string, toUtc?: string) => {
    const qs = [fromUtc ? `fromUtc=${encodeURIComponent(fromUtc)}` : '',
                toUtc ? `toUtc=${encodeURIComponent(toUtc)}` : '']
      .filter(Boolean).join('&')
    return request<BasketKtIntradayData[]>(`/api/synthetic/${fund}/kt-intraday${qs ? `?${qs}` : ''}`)
  },
  /** Fund-parameterised counterpart of `bgaKtNowPence` — see `getSyntheticKt`. */
  getSyntheticKtNowPence: (fund: 'bga' | 'jh' | 'gold' | 'gge') =>
    request<KtNowPenceData>(`/api/synthetic/${fund}/kt-now-pence`),
  /**
   * K_t vs official-NAV tracking scoreboard (MAE/RMSE/IC per method) for
   * any fund on the fund-parameterised /api/synthetic/{fund}/kt-tracking
   * route. `honest` window semantics are per-fund (JH: 2025-10-31 Stage-0
   * cutover; gold: 2026-07-25 fresh-data era; BGA: full history + note).
   */
  ktTracking: (fund: 'bga' | 'jh' | 'gold' | 'gge', window: 'trailing60' | 'honest' = 'trailing60') =>
    request<KtTrackingResponse>(`/api/synthetic/${fund}/kt-tracking?window=${window}`),
  /**
   * K_t tracking scoreboard for all four funds in one call (bga/jh/gge/gold)
   * over a trailing session window. See `KtDailyScoreboard` above.
   */
  getKtDailyScoreboard: (windowSessions: number = 60) =>
    request<KtDailyScoreboard>(`/api/kt/daily-scoreboard?window=${windowSessions}`),
  pricingSource: () => request<PricingSourceResponse>('/api/risk/pricing-source'),
  signalHealth: () => request<SignalHealthResponse>('/api/risk/signal-health'),
  potParity: (fund?: string) => request<PotParityResponse>(fund ? `/api/risk/pot-parity?fund=${encodeURIComponent(fund)}` : '/api/risk/pot-parity'),
  potParityFunds: () => request<PotParityFundEntry[]>('/api/risk/pot-parity/funds'),
  riskRules: () => request<RiskRulesResponse>('/api/risk/rules'),
  edgeMap: (window: 120 | 250) => request<EdgeMapResponse>(`/api/risk/edge-map?window=${window}`),
  edgeSource: (fund: string, window: 120 | 250) =>
    request<EdgeSourceResponse>(`/api/risk/edge-source?fund=${encodeURIComponent(fund)}&window=${window}`),
  devopsFindings: () => request<DevOpsFindingsResponse>('/api/devops/findings'),
  // Superuser acting as themselves only (403 otherwise). PUT upserts the day's mark.
  devopsAcknowledge: (agent: string, date: string, body: { note: string; reference?: string | null }) =>
    request<AgentFindingAckResponse>(`/api/devops/findings/${agent}/${date}/ack`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  devopsUnacknowledge: (agent: string, date: string) =>
    request<AgentFindingAckResponse>(`/api/devops/findings/${agent}/${date}/ack`, { method: 'DELETE' }),
  /**
   * BGA/JH step-aside anchor state — durable prior US-close snapshot plus
   * a freshness classification ("fresh"/"aging"/"stale"/"missing"). Powers
   * StepAsideAnchorTile.vue; server-side classification uses the same
   * threshold the health monitor alarms against, so the dashboard and the
   * WhatsApp alert can never disagree on "why is this red".
   */
  getStepAsideAnchorState: () =>
    request<StepAsideAnchorState>('/api/system/step-aside/anchor-state'),
  userTrades: () => request<any[]>('/api/user/subledger/trades'),
  /** Fund-aware balance-sheet. Omit fund to use the server-resolved active fund. */
  subledgerBalanceSheet: (opts: { fund?: string; date?: string } = {}): Promise<BalanceSheetData> => {
    const q = new URLSearchParams()
    if (opts.fund) q.set('fund', opts.fund)
    if (opts.date) q.set('date', opts.date)
    const qs = q.toString()
    return request<BalanceSheetData>(`/api/user/subledger/balance-sheet${qs ? '?' + qs : ''}`)
  },
  /** Fund-aware trades list. Omit fund to use the server-resolved active fund. */
  subledgerTrades: (opts: { fund?: string } = {}): Promise<any[]> => {
    const q = new URLSearchParams()
    if (opts.fund) q.set('fund', opts.fund)
    const qs = q.toString()
    return request<any[]>(`/api/user/subledger/trades${qs ? '?' + qs : ''}`)
  },
  pnlSummary: (fund?: 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'OTP') => {
    const url = fund ? `/api/user/subledger/pnl-summary?fund=${fund.toLowerCase()}` : '/api/user/subledger/pnl-summary';
    return request<{
      total: number
      byFund: Array<{ fund_id: string; pnl: number; periodFrom: string; periodTo: string }>
    }>(url);
  },
  // Gate row (`hl_reconciliation_gate`, not sidecar) — kept for a future
  // top-level gate-status badge in the UI (ReconciledPanel was deleted in
  // PR ζ but the underlying endpoint is unchanged).
  reconciledGate: () =>
    request<{
      status: 'GREEN' | 'AMBER' | 'RED' | 'PENDING'
      deltaGbp: number
      toleranceGbp: number
      reconstructedNpv: number
      hlSnapshotNpv: number
      lastCheckedAt: string | null
      detail: string | null
    }>('/api/user/reconciled/gate'),
  // Auth
  // HL SMS verification (2026-09-21): /login answers 202 { verificationRequired,
  // maskedPhone } when HL wants the code it texted; /login/verify completes it.
  authLogin: (email: string, hlUserId?: string, dob?: string, password?: string, secureNumber?: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, hlUserId, dob, password, secureNumber }),
    }),
  authLoginVerify: (email: string, code: string, hlUserId?: string) =>
    request<UserProfile>('/api/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code, hlUserId }),
    }),
  authLoginResend: (email: string, hlUserId?: string) =>
    request<LoginVerificationPending>('/api/auth/login/resend', {
      method: 'POST',
      body: JSON.stringify({ email, hlUserId }),
    }),
  // Whether the signed-in user's HL session is usable; drives the Account
  // panel's "reconnect HL" card after an API restart or a parked login.
  authHlStatus: () => request<HlStatus>('/api/auth/hl-status'),
  authLogout: () =>
    request<{ loggedOut: boolean }>('/api/auth/logout', { method: 'POST' }),
  authMe: () => request<UserProfile>('/api/auth/me'),
  // Strategies
  strategiesPublished: () => request<StrategyRecord[]>('/api/strategies/published'),
  strategiesPublish: (configName: string, strategyName: string, sharpeRatio?: number) =>
    request<{ strategyId: string }>('/api/strategies/publish', {
      method: 'POST',
      body: JSON.stringify({ configName, strategyName, sharpeRatio }),
    }),
  strategiesUnpublish: (strategyId: string) =>
    request<{ unpublished: boolean }>(`/api/strategies/unpublish/${strategyId}`, { method: 'POST' }),
  strategiesSwitch: (strategyId: string) =>
    request<{ switched: boolean }>(`/api/strategies/switch/${strategyId}`, { method: 'POST' }),
  strategiesActive: () => request<{ active: StrategyRecord | null }>('/api/strategies/active'),
  // Admin
  adminUsers: () => request<AdminUserRecord[]>('/api/admin/users'),
  adminCreateUser: (email: string) =>
    request<AdminUserRecord>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  adminDeactivateUser: (userId: string) =>
    request<{ deactivated: boolean }>(`/api/admin/users/${userId}/deactivate`, { method: 'POST' }),
  adminJobs: (limit?: number) =>
    request<any[]>(`/api/admin/jobs${limit ? `?limit=${limit}` : ''}`),
  // Superuser-only: set (digits, spaces, '+', '-', '()' accepted) or clear
  // (null) a user's WhatsApp number for their private account alerts.
  adminSetUserWhatsapp: (userId: string, number: string | null) =>
    request<{ userId: string; hasWhatsapp: boolean; whatsappNumberMasked: string | null }>(
      `/api/admin/users/${userId}/whatsapp`,
      { method: 'PUT', body: JSON.stringify({ number }) },
    ),
  adminReconcileUser: (userId: string) =>
    request<{ message: string }>(`/api/admin/users/${userId}/reconcile`, { method: 'POST' }),
  // Task 6 (spec 2026-09-28 linked users): add a read-only viewer linked
  // under the calling superuser's guardianship, and the switcher list.
  adminCreateLinkedUser: (hlUsername: string, displayName: string) =>
    request<LinkedUserRecord>('/api/admin/linked-users', {
      method: 'POST',
      body: JSON.stringify({ hlUsername, displayName }),
    }),
  adminSwitchableUsers: () => request<SwitchableUser[]>('/api/admin/switchable-users'),
  // Spec 2026-09-30 per-user fund access. Live funds via fund-access; a gated fund (JAPAN today)
  // via the EXISTING entitlement endpoints (grant = PUT, revoke = DELETE; the server 409s a
  // revoke while held). `fund` accepts any access-grid fund — the caller's isGatedFund check
  // routes gated funds to the entitlement endpoints instead; the server itself 400s a gated fund
  // sent here, so this type doesn't need to exclude one by name.
  adminFundAccess: () => request<FundAccessRow[]>('/api/admin/fund-access'),
  adminSetFundAccess: (userId: string, fund: FundAccessFund, allowed: boolean) =>
    request<{ userId: string; fund: string; allowed: boolean; changed: boolean }>(
      `/api/admin/users/${userId}/fund-access/${fund}`,
      { method: 'PUT', body: JSON.stringify({ allowed }) },
    ),
  adminGrantEntitlement: (userId: string, fund: FundAccessFund) =>
    request<{ user_id: string; fund: string; granted: boolean }>(
      `/api/admin/users/${userId}/fund-entitlements/${fund}`, { method: 'PUT', body: '{}' },
    ),
  adminRevokeEntitlement: (userId: string, fund: FundAccessFund) =>
    request<{ user_id: string; fund: string; revoked: boolean }>(
      `/api/admin/users/${userId}/fund-entitlements/${fund}`, { method: 'DELETE' },
    ),
  // Fix (linked users, HL user ID correction): superuser corrects a linked
  // user's HL login user ID — evicts her stale HL credentials/session/
  // parked login server-side (see LinkedUserAdminEndpoints.cs), so the
  // note "signs her out of HL — reconnect her afterwards" is accurate.
  adminUpdateLinkedUserHlUsername: (userId: string, hlUsername: string) =>
    request<AdminUserRecord>(`/api/admin/linked-users/${userId}/hl-username`, {
      method: 'PUT',
      body: JSON.stringify({ hlUsername }),
    }),
  // Task 7 (linked users): superuser connects ANOTHER user's HL. The HL user
  // ID is the target's stored one (not sent); neither call sets a session
  // cookie, so the superuser's own sign-in is never replaced.
  adminHlConnect: (userId: string, dob: string, password: string, secureNumber: string) =>
    request<LoginVerificationPending | { connected: true }>(`/api/admin/users/${userId}/hl/connect`, {
      method: 'POST',
      body: JSON.stringify({ dob, password, secureNumber }),
    }),
  adminHlVerify: (userId: string, code: string) =>
    request<{ connected: true }>(`/api/admin/users/${userId}/hl/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  adminOtpManualClose: (
    userId: string,
    instrument: 'LQQ3' | 'LQQS',
    body: { exitPriceGbp: number; fillAtUtc?: string | null },
  ) =>
    request<{
      hedgeTradeId: number
      reconciliationJobId: string
      unitsClosed: number
      weightedAvgEntryGbp: number
      exitPriceGbp: number
      grossPnlGbp: number
      netPnlGbp: number
      commissionGbp: number
      redisKeyExisted: boolean
    }>(`/api/admin/users/${userId}/otp/${instrument}/manual-close`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminJhgtlForceRefresh: () =>
    request<{
      newRows: number
      skippedTickers: string[]
      errors: Array<{ ticker: string; error: string }>
      tickersProcessed: number
    }>('/api/admin/holdings/jhgtl/force-refresh', { method: 'POST' }),
  // Fund shortlist (PR 2b) — /api/admin/strategy/fund-shortlist/* (FundShortlistEndpoints.cs).
  fundShortlist: () => request<FundShortlistResponse>('/api/admin/strategy/fund-shortlist'),
  fundShortlistHistory: (limit = 200) => request<ShortlistChangeRow[]>(`/api/admin/strategy/fund-shortlist/history?limit=${limit}`),
  fundShortlistCollectorRuns: (limit = 30) => request<ShortlistCollectorRun[]>(`/api/admin/strategy/fund-shortlist/collector-runs?limit=${limit}`),
  fundShortlistProspectorRuns: (limit = 20) => request<ProspectorRunRow[]>(`/api/admin/strategy/fund-shortlist/prospector-runs?limit=${limit}`),
  fundShortlistLevyChecks: () => request<LevyCheckSummaryRow[]>('/api/admin/strategy/fund-shortlist/levy-checks'),
  fundShortlistScoreNow: () => request<{ started: boolean }>('/api/admin/strategy/fund-shortlist/score-now', { method: 'POST' }),
  fundShortlistReseed: () => request<{ changed: number }>('/api/admin/strategy/fund-shortlist/reseed', { method: 'POST' }),
  fundShortlistResetCeiling: (fundKey: string) =>
    request<{ reset: boolean }>(`/api/admin/strategy/fund-shortlist/${encodeURIComponent(fundKey)}/reset-ceiling`, { method: 'POST' }),
  // Strategy admin (Task 8) — /api/admin/strategy/* (StrategyAdminEndpoints.cs).
  // Super-user only server-side; every endpoint 404s to a non-super caller.
  adminStrategyLibrary: () => request<StrategyCatalogEntry[]>('/api/admin/strategy/library'),
  adminStrategyUsers: () => request<UserStrategyRow[]>('/api/admin/strategy/users'),
  adminStrategyUserHistory: (userId: string) =>
    request<UserStrategyHistoryEntry[]>(`/api/admin/strategy/users/${userId}/history`),
  adminStrategySwitch: (
    userId: string,
    // tiers (2026-10-05): written to the user's allocation rows with a live shared_pot_v1 assignment.
    body: { strategy_id: string; params: Record<string, unknown>; reason?: string | null; tiers?: Record<string, number> },
  ) =>
    request<UserStrategyAssignment>(`/api/admin/strategy/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  adminStrategySignals: (since?: string) => {
    const url = since
      ? `/api/admin/strategy/signals?since=${encodeURIComponent(since)}`
      : '/api/admin/strategy/signals';
    return request<StrategyEventRow[]>(url);
  },
  adminStrategyEvents: (since?: string) => {
    const url = since
      ? `/api/admin/strategy/events?since=${encodeURIComponent(since)}`
      : '/api/admin/strategy/events';
    return request<StrategyEventRow[]>(url);
  },
  // Risk — 2026-08-05 risk-screen-per-fund: no active_fund filter. Each GET
  // returns rows across ALL fund_ids for the user in one call; RiskPanel
  // splits by row.stream ('bga'/'jh'/'gold'/'combined') into per-fund tiles.
  riskMetrics: (days?: number) => {
    const q = new URLSearchParams();
    if (days) q.set('days', String(days));
    const qs = q.toString();
    return request<DailyRiskMetricRow[]>(`/api/risk/metrics${qs ? `?${qs}` : ''}`);
  },
  riskBaselines: () => request<RiskBaseline[]>('/api/risk/baselines'),
  riskThresholds: () => request<RiskThreshold[]>('/api/risk/thresholds'),
  riskUpdateThresholds: (thresholds: RiskThreshold[]) =>
    request<void>('/api/risk/thresholds', {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    }),
  // OTP Tab
  otpSummary: (fund?: 'BGA' | 'JH' | 'OTP') => {
    const url = fund ? `/api/otp/summary?fund=${fund.toLowerCase()}` : '/api/otp/summary';
    return request<any>(url);
  },
  otpPosition: (fund?: 'BGA' | 'JH' | 'OTP') => {
    const url = fund ? `/api/otp/position?fund=${fund.toLowerCase()}` : '/api/otp/position';
    return request<any>(url);
  },
  otpTrades: (limit?: number, fund?: 'BGA' | 'JH' | 'OTP') => {
    const q = new URLSearchParams();
    if (limit) q.set('limit', String(limit));
    if (fund) q.set('fund', fund.toLowerCase());
    const qs = q.toString();
    return request<any[]>(`/api/otp/trades${qs ? `?${qs}` : ''}`);
  },
  otpCircuitBreaker: (fund?: 'BGA' | 'JH' | 'OTP') => {
    const url = fund ? `/api/otp/circuit-breaker?fund=${fund.toLowerCase()}` : '/api/otp/circuit-breaker';
    return request<any>(url);
  },
  // OTP standalone Plan C, Task 2: gate-state visibility (superuser-only —
  // mirrors OtpGateService.GetStateAsync). 204/no body when no gate exists
  // for today.
  otpGate: () => request<OtpGateView | null>('/api/otp/gate'),
  // OTP standalone Plan C, Task 3: Plan B's component registry (Program.cs
  // /api/otp/registry, superuser-only). PUT surfaces a 400 with a plain
  // { error } message body on a Σ-target-pct-over-1.0 violation — the
  // shared request() helper throws Error(body.error) for non-ok responses,
  // so callers read e.message.
  otpRegistry: () => request<OtpRegistryRow[]>('/api/otp/registry'),
  otpRegistryUpdate: (otpType: string, body: { enabled?: boolean; targetPct?: number }) =>
    request<{ updated: string }>(`/api/otp/registry/${otpType}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  otpFireStatus: (fund?: 'BGA' | 'JH' | 'OTP') => {
    const url = fund ? `/api/otp/fire-status?fund=${fund.toLowerCase()}` : '/api/otp/fire-status';
    return request<{
      asOf: string
      triggers: Array<{
        triggerEventId: string
        triggerAt: string
        otpType: string
        instrument: string | null
        jobId: string | null
        jobStatus: string | null
        tradeId: number | null
        outcome: 'ok' | 'pending' | 'unconfirmed'
        unconfirmedReason: string | null
        unconfirmedDetail: string | null
      }>
      anyUnconfirmed: boolean
    }>(url);
  },
  // OTP shadow-book status + kill-switch (Phase A, spec 2026-09-06).
  // GET returns all seven shadow books' enabled / decision / open-position
  // state (cf-nq-v2 added 2026-09-06 alongside cf-nq) plus the shared
  // allocator's slot snapshot. POST flips a book's Redis kill-switch
  // override (super-user only).
  otpShadowStatus: () => request<{
    books: Array<{
      key: string
      name: string
      instrument: string
      direction: string
      enabledFileConfig: boolean
      killSwitchOverride: string | null
      effectiveEnabled: boolean
      lastDecisionRow: { date: string; fired: boolean; skipReason: string | null } | null
      currentOpenPosition: {
        entryDate: string | null
        synthPrice: number | null
        allocatorSlotIndex: number | null
      } | null
      lastFireAtUtc: string | null
      studyExpectedSharpe: number
      allocatorSlot: {
        slotIndex: number
        holder: string | null
        expectedExitDate: string | null
        notionalGbp: number
      } | null
      crosscheck?: {
        runDate: string
        runAtUtc: string
        divergences: number
        summary: string | null
        latestDivergence: string | null
        error: string | null
      } | null
    }>
    allocator: {
      freeSlots: number
      busySlots: number
      totalSlots: number
      poolNotionalGbp: number
      slotNotionalGbp: number
      holders: Array<{
        slotIndex: number
        holder: string | null
        expectedExitDate: string | null
        notionalGbp: number
      }>
    }
  }>('/api/otp/shadow/status'),
  otpShadowKillSwitch: (body: { book: string; setTo: boolean | null }) =>
    request<{ book: string; setTo: boolean | null }>('/api/otp/shadow/kill_switch', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Charts
  chartSentimentHistory: (window?: string) =>
    request<SentimentHistoryResponse>(`/api/charts/sentiment-history${window ? `?window=${window}` : ''}`),
  /** VIX term-structure history — backs the regime mini-chart and the
   * /charts/vixregime view. Returns one row per trading date with M1, M2,
   * M3, computed contango_pct, spot VIX and regime label (low/normal/high/extreme). */
  vixTermStructureHistory: (from?: string, to?: string) => {
    const qs = [from ? `from=${from}` : '', to ? `to=${to}` : ''].filter(Boolean).join('&')
    return request<Array<{
      date: string
      m1: number
      m2: number | null
      m3: number | null
      contangoPct: number | null
      spotVix: number | null
      regime: 'extreme' | 'high' | 'normal' | 'low' | 'unknown'
      spotVsM1Pct: number | null
    }>>(`/api/vix/term-structure/history${qs ? `?${qs}` : ''}`)
  },
  newsDailyTickerSummary: (date?: string, limit?: number) => {
    const qs: string[] = []
    if (date) qs.push(`date=${date}`)
    if (limit) qs.push(`limit=${limit}`)
    return request<SentimentTickerSummaryResponse>(
      `/api/news/daily-ticker-summary${qs.length ? `?${qs.join('&')}` : ''}`)
  },
  chartSnapshots: (window?: string, from?: string, to?: string, fund?: 'BGA' | 'JH') => {
    const params = new URLSearchParams()
    if (window) params.set('window', window)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    // Fund routing (V056 / 2026-06-24): default + 'BGA' hits the legacy BGA
    // table; 'JH' hits jh_hourly_snapshots via the JH snapshot repos.
    if (fund) params.set('fund', fund)
    const qs = params.toString()
    return request<HourlySnapshotResponse>(`/api/charts/hourly-snapshots${qs ? `?${qs}` : ''}`)
  },
  formingCandle: async (window?: string, fund?: 'BGA' | 'JH'): Promise<FormingCandleResponse | null> => {
    // 204 No Content = server has no in-memory state yet (pre-market, cold
    // start, or before the first 60s sample tick). Return null so the
    // caller falls back to live-tick-only synthesis. Matches the `request`
    // helper's auth/base-URL behaviour but avoids its res.json() which
    // would blow up on 204.
    //
    // The `window` param routes to the matching sampler: "24h" reads the
    // 15-min QuarterHourlySnapshotService's in-progress bar, others read
    // the 60-min HourlySnapshotService's bar. Without this branch a 24h
    // view would mix 15-min historical candles with an HOUR-LONG forming
    // bar — totally inconsistent x-axis spacing.
    const params = new URLSearchParams()
    if (window) params.set('window', window)
    if (fund) params.set('fund', fund)
    const qs = params.toString() ? `?${params.toString()}` : ''
    const res = await fetch(`${BASE}/api/charts/forming-candle${qs}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (res.status === 204) return null
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error ?? `HTTP ${res.status}`)
    }
    return res.json()
  },
  newsTicker: (limit = 20) =>
    request<NewsTickerItem[]>(`/api/news/ticker?limit=${limit}`),
};

/**
 * Standalone fetch for /api/gold/synth-nav-live — returns null on 404,
 * throws on other errors. Used by useGoldLiveSynthNav composable.
 */
export async function fetchGoldSynthNavLive(): Promise<GoldSynthNavLive | null> {
  return api.goldSynthNavLive();
}

/**
 * Standalone fetch for /api/gge/synth-nav-live — returns null on 404,
 * throws on other errors. Used by useGgeLiveSynthNav composable.
 */
export async function fetchGgeSynthNavLive(): Promise<GgeSynthNavLive | null> {
  return api.ggeSynthNavLive();
}

/** The shared live K_t routes (LiveSynthNavEndpoints): /api/{fund}/synth-nav-live and /api/{fund}/kt-intraday. */
export const liveSynthNavUrl = (fund: LiveSynthFund) => `/api/${fund}/synth-nav-live?fund=${fund.toUpperCase()}`
export const liveKtIntradayUrl = (fund: LiveSynthFund, window: '1d' | '5d') =>
  `/api/${fund}/kt-intraday?fund=${fund.toUpperCase()}&window=${window}`

const LIVE_SYNTH_FETCH: Record<LiveSynthFund, () => Promise<LiveSynthNav>> = {
  charteris: () => api.charterisSynthNavLive(),
  amati: () => api.amatiSynthNavLive(),
}

/**
 * Standalone fetch for /api/{fund}/synth-nav-live — returns null on 404
 * (switch off or not entitled), throws on other errors. The payload type is
 * snake_case (raw), so no field remap. Used by useFundLiveSynthNav.
 */
export async function fetchFundSynthNavLive(fund: LiveSynthFund): Promise<LiveSynthNav | null> {
  try {
    const r = await LIVE_SYNTH_FETCH[fund]();
    // Guard the shape: a non-object 200 (e.g. [] from a catch-all stub, or an unexpected body)
    // must read as "no data", not a half-rendered card. synth_nav_pence is the required field.
    return r && typeof (r as LiveSynthNav).synth_nav_pence === 'number' ? (r as LiveSynthNav) : null;
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null;
    throw e;
  }
}
/** /api/charteris/synth-nav-live (see fetchFundSynthNavLive). */
export const fetchCharterisSynthNavLive = (): Promise<LiveSynthNav | null> => fetchFundSynthNavLive('charteris');
/** /api/amati/synth-nav-live (Amati live tile, 2026-10-15; 404 while Amati:KtLiveEnabled is off). */
export const fetchAmatiSynthNavLive = (): Promise<LiveSynthNav | null> => fetchFundSynthNavLive('amati');

